<template>
  <div class="bg pb-24 bg-gray-900 p-3 h-full overflow-auto scrollbar-custom">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-indigo-300">
        <i class="fas fa-book mr-2"></i> วิธีเล่น
      </h1>
    </div>

    <!-- Game Overview -->
    <section class="bg-gray-900 bg-opacity-80 rounded-md p-4 mb-6 border border-gray-600 animate-fade-in">
      <h2 class="text-lg font-semibold text-indigo-200 mb-3">
        <i class="fas fa-gamepad mr-2"></i> ภาพรวมของเกม
      </h2>
      <p class="text-indigo-200 text-sm">
        ในโลกที่เต็มไปด้วยซอมบี้ คุณต้องต่อสู้เพื่อเอาชีวิตรอด! ค้นหาทรัพยากร อัปเกรดตัวละคร และสำรวจสถานที่ต่างๆ เพื่อกำจัดซอมบี้ บริหารทรัพยากร และพยายามเอาตัวรอดในโลกที่แสนโหดร้าย
      </p>
    </section>

    <!-- Character Status -->
    <section class="space-y-6">
      <h2 class="text-lg font-semibold text-indigo-200 mb-4">
        <i class="fas fa-user mr-2"></i> ระบบสถานะตัวละคร
      </h2>

      <!-- Energy -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-bolt mr-2"></i> พลังงาน (Energy)
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">พลังงานคืออะไร?</span> พลังงานเอาไว้สำหรับ สำรวจ โจมตีซอมบี้ และอื่นๆภายในเกม</p>
          <p><span class="text-indigo-400">ได้พลังงานมายังไง?</span> 600 หน่วยทุก 3 ชั่วโมง (สูงสุด 4800 หน่วย)</p>
        </div>
      </article>

      <!-- Food -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-utensils mr-2"></i> อาหาร (Food)
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">อาหารคืออะไร?</span> จำนวนอาหารทั้งหมด ถ้าถึง 0 คุณจะตาย</p>
          <p><span class="text-indigo-400">อาหารลดลงยังไง?</span> ลด 10 หน่วยทุก 3 ชั่วโมง กินอาหารเพื่อเติมระดับ</p>
          <p><span class="text-indigo-400">ถ้าอาหารหมดจะเป็นยังไง?</span> จำนวนอาหารทั้งหมด ถ้าถึง 0 คุณจะตาย</p>
        </div>
      </article>

      <!-- Happiness -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-smile mr-2"></i> ความสุข (Happiness)
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">ความสุขคืออะไร?</span> ระดับความสุข ถ้าถึง 0 คุณจะตาย</p>
          <p><span class="text-indigo-400">ความสุขลดลงยังไง?</span> ลดลงตามเหตุการณ์ในเกม</p>
          <p><span class="text-indigo-400">ถ้าความสุขหมดจะเป็นยังไง?</span> ระดับความสุข ถ้าถึง 0 คุณจะตาย</p>
        </div>
      </article>

      <!-- Infection -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-biohazard mr-2"></i> เชื้อโรค (Infection)
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">เชื้อโรคคืออะไร?</span> ระดับการติดเชื้อ ถ้าถึง 100 คุณจะตาย</p>
          <p><span class="text-indigo-400">ติดเชื้อได้ยังไง?</span> ถูกซอมบี้โจมตีมีโอกาสติดเชื้อ ลดได้ด้วยยารักษา</p>
          <p><span class="text-indigo-400">ถ้าเชื้อโรคถึงขีดสุดจะเป็นยังไง?</span> ระดับการติดเชื้อ ถ้าถึง 100 คุณจะตาย</p>
        </div>
      </article>
    </section>

    <!-- Character Stats -->
    <section class="space-y-6 mt-6">
      <h2 class="text-lg font-semibold text-indigo-200 mb-4">
        <i class="fas fa-chart-bar mr-2"></i> ค่าพลังตัวละคร
      </h2>

      <!-- maxHp -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-heart mr-2"></i> พลังชีวิตสูงสุด (maxHp)
        </h3>
        <div class="text-indigo-200 text-sm">
          <table class="w-full border-collapse mb-3">
            <tbody>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">ความหมาย</td>
                <td class="py-1">จำนวนพลังชีวิตทั้งหมด ถ้าถึง 0 คุณจะตาย</td>
              </tr>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">แหล่งที่มา</td>
                <td class="py-1">สเตมิน่า (ไอเทม, สเตตัส, สกิล, ฯลฯ) × 20 + maxHp จากไอเทม</td>
              </tr>
              <tr>
                <td class="py-1 text-indigo-400">ตัวอย่าง</td>
                <td class="py-1">สเตมิน่า 15 = 300, ไอเทม +50 = 350</td>
              </tr>
            </tbody>
          </table>
          <p><span class="text-indigo-400">เพิ่มเติม:</span> พลังชีวิตสูงช่วยให้คุณทนต่อการโจมตีจากซอมบี้ได้นานขึ้น</p>
        </div>
      </article>

      <!-- atk -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-fist-raised mr-2"></i> พลังโจมตี (atk)
        </h3>
        <div class="text-indigo-200 text-sm">
          <table class="w-full border-collapse mb-3">
            <tbody>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">ความหมาย</td>
                <td class="py-1">จำนวนพลังโจมตีทั้งหมด</td>
              </tr>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">แหล่งที่มา</td>
                <td class="py-1">Strength (ไอเทม, สเตตัส, สกิล, ฯลฯ) × 2 + atk จากไอเทม</td>
              </tr>
              <tr>
                <td class="py-1 text-indigo-400">ตัวอย่าง</td>
                <td class="py-1">Strength 10 = 20, ไอเทม +5 = 25</td>
              </tr>
            </tbody>
          </table>
          <p><span class="text-indigo-400">เพิ่มเติม:</span> atk สูงทำให้ฆ่าซอมบี้ได้เร็วและง่ายขึ้น</p>
        </div>
      </article>

      <!-- critical -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-bullseye mr-2"></i> โอกาสคริติคอล (critical)
        </h3>
        <div class="text-indigo-200 text-sm">
          <table class="w-full border-collapse mb-3">
            <tbody>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">ความหมาย</td>
                <td class="py-1">จำนวนโอกาสคริติคอลทั้งหมด</td>
              </tr>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">แหล่งที่มา</td>
                <td class="py-1">Intelligence (ไอเทม, สเตตัส, สกิล, ฯลฯ) ÷ 2 + critical จากไอเทม ÷ 100</td>
              </tr>
              <tr>
                <td class="py-1 text-indigo-400">ตัวอย่าง</td>
                <td class="py-1">Intelligence 20 ÷ 2 = 10, ไอเทม +2 = 12%</td>
              </tr>
            </tbody>
          </table>
          <p><span class="text-indigo-400">เพิ่มเติม:</span> critical สูงช่วยให้ตีแรงบ่อย ฆ่าซอมบี้ไวขึ้น</p>
        </div>
      </article>

      <!-- evasion -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-running mr-2"></i> โอกาสหลบ (evasion)
        </h3>
        <div class="text-indigo-200 text-sm">
          <table class="w-full border-collapse mb-3">
            <tbody>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">ความหมาย</td>
                <td class="py-1">จำนวนโอกาสหลบทั้งหมด</td>
              </tr>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">แหล่งที่มา</td>
                <td class="py-1">Agility (ไอเทม, สเตตัส, สกิล, ฯลฯ) ÷ 2 + evasion จากไอเทม ÷ 100</td>
              </tr>
              <tr>
                <td class="py-1 text-indigo-400">ตัวอย่าง</td>
                <td class="py-1">Agility 16 ÷ 2 = 8, ไอเทม +4 = 12%</td>
              </tr>
            </tbody>
          </table>
          <p><span class="text-indigo-400">เพิ่มเติม:</span> evasion สูงช่วยให้คุณโดนตีน้อยลง อยู่รอดนานขึ้น</p>
        </div>
      </article>

      <!-- defense -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-shield-alt mr-2"></i> พลังป้องกัน (defense)
        </h3>
        <div class="text-indigo-200 text-sm">
          <table class="w-full border-collapse mb-3">
            <tbody>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">ความหมาย</td>
                <td class="py-1">จำนวนพลังป้องกันทั้งหมด</td>
              </tr>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">แหล่งที่มา</td>
                <td class="py-1">Defense จากไอเทม (เช่น เกราะ)</td>
              </tr>
              <tr>
                <td class="py-1 text-indigo-400">ตัวอย่าง</td>
                <td class="py-1">เกราะ defense 10 = ลดดาเมจ 10 หน่วย</td>
              </tr>
            </tbody>
          </table>
          <p><span class="text-indigo-400">เพิ่มเติม:</span> defense สูงช่วยให้ได้รับความเสียหายน้อยลง</p>
        </div>
      </article>

      <!-- criticalReduction -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-shield-virus mr-2"></i> ป้องกันคริติคอล (criticalReduction)
        </h3>
        <div class="text-indigo-200 text-sm">
          <table class="w-full border-collapse mb-3">
            <tbody>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">ความหมาย</td>
                <td class="py-1">จำนวนลดโอกาสคริติคอลที่ได้รับจากศัตรูทั้งหมด</td>
              </tr>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">แหล่งที่มา</td>
                <td class="py-1">ค่าป้องกัน 20 = 1 % ป้องกันคริติคอล</td>
                <td class="py-1">Luck (ไอเทม, สเตตัส, สกิล, ฯลฯ) ÷ 2</td>
              </tr>
              <tr>
                <td class="py-1 text-indigo-400">ตัวอย่าง</td>
                <td class="py-1">Luck 20 ÷ 2 = 10%</td>
              </tr>
            </tbody>
          </table>
          <p><span class="text-indigo-400">เพิ่มเติม:</span> criticalReduction สูงช่วยลดโอกาสโดนคริติคอลจากศัตรู ทำให้ได้รับดาเมจน้อยลง</p>
        </div>
      </article>

      <!-- criticalReduction -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-shield-virus mr-2"></i> ลดดาเมจ (damageReduction)
        </h3>
        <div class="text-indigo-200 text-sm">
          <table class="w-full border-collapse mb-3">
            <tbody>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">ความหมาย</td>
                <td class="py-1">จะได้รับดาเมจที่ลดลงจากการถูกโจมตี</td>
              </tr>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">แหล่งที่มา</td>
                <td class="py-1">ค่าป้องกัน 20 = 1 % ป้องกันคริติคอล</td>
              </tr>
              <tr>
                <td class="py-1 text-indigo-400">ตัวอย่าง</td>
                <td class="py-1">พลังป้องกัน 20 = ลดดาเมจ 1%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>

      <!-- dropRateBonus -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-gift mr-2"></i> โบนัสดรอป (dropRateBonus)
        </h3>
        <div class="text-indigo-200 text-sm">
          <table class="w-full border-collapse mb-3">
            <tbody>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">ความหมาย</td>
                <td class="py-1">จำนวนโบนัสดรอปไอเทมทั้งหมด</td>
              </tr>
              <tr class="border-b border-gray-600">
                <td class="py-1 text-indigo-400">แหล่งที่มา</td>
                <td class="py-1">Luck (ไอเทม, สเตตัส, สกิล, ฯลฯ) × 0.01</td>
              </tr>
              <tr>
                <td class="py-1 text-indigo-400">ตัวอย่าง</td>
                <td class="py-1">Luck 100 × 0.01 = 1%</td>
              </tr>
            </tbody>
          </table>
          <p><span class="text-indigo-400">เพิ่มเติม:</span> dropRateBonus สูงช่วยให้ได้รับไอเทมมากขึ้นจากการต่อสู้หรือสำรวจ</p>
        </div>
      </article>
    </section>

    <!-- Exploration and Punishment System -->
    <section class="space-y-6 mt-6">
      <h2 class="text-lg font-semibold text-indigo-200 mb-4">
        <i class="fas fa-map-marked-alt mr-2"></i> ระบบสำรวจและการลงโทษ
      </h2>

      <!-- Exploration -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-compass mr-2"></i> การสำรวจแผนที่
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">การสำรวจคืออะไร?</span> การออกสำรวจแผนที่เพื่อต่อสู้กับซอมบี้และรับรางวัล โดยใช้ทรัพยากรต่างๆ เช่น พลังงาน, เพชร, หรือแคช</p>
          <p><span class="text-indigo-400">สำรวจได้ยังไง?</span> เลือกแผนที่ในเกม ต้องมีทรัพยากรเพียงพอ เช่น เลเวล, ชื่อเสียง, ห้อง, เพชร, เหรียญเงิน, ทอง, แคช, และพลังงานตามที่แผนที่กำหนด</p>
          <div>
            <span class="text-indigo-400">ต้องใช้ทรัพยากรอะไรบ้าง?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>เลเวลตัวละคร, ชื่อเสียง, จำนวนห้อง (ถ้ามี)</li>
              <li>ทรัพยากร เช่น เพชร, เหรียญเงิน, ทอง, แคช, พลังงาน</li>
              <li>อาจลดความสุขหลังสำรวจ (ขึ้นอยู่กับแผนที่)</li>
            </ul>
          </div>
          <div>
            <span class="text-indigo-400">ผลลัพธ์ของการสำรวจ?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>เริ่มอีเวนต์ต่อสู้ซอมบี้ (มีเวลา 10 นาที)</li>
              <li>พบซอมบี้จำนวนสุ่มตามแผนที่</li>
              <li>อาจได้รับรางวัล เช่น ไอเทม (หมวก, อาวุธ, หนังสือ) ซึ่งบางชิ้นสามารถโอนได้</li>
            </ul>
          </div>
          <p><span class="text-indigo-400">เคล็ดลับ:</span> ตรวจสอบทรัพยากรก่อนสำรวจ และเตรียมพร้อมต่อสู้กับซอมบี้ให้ทันเวลา!</p>
        </div>
      </article>

      <!-- Punishment for Expired Events -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-hourglass-end mr-2"></i> การลงโทษเมื่อสำรวจไม่ทันเวลา
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">การลงโทษคืออะไร?</span> หากไม่สามารถเคลียร์อีเวนต์สำรวจ (ต่อสู้ซอมบี้) ภายใน 10 นาที ตัวละครจะถูกลงโทษและตาย</p>
          <div>
            <span class="text-indigo-400">บทลงโทษมีอะไรบ้าง?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>โดยจะสูญเสียไอเทม(เสีย 0-1), ทักษะ(เสีย 0), รอยสัก(เสีย 0-1) และห้องจำนวน(เสีย 0-1)</li>
            </ul>
          </div>
          <p><span class="text-indigo-400">ผลกระทบ?</span> คุณต้องยอมรับบทลงโทษในอีเวนต์ "เกิดใหม่" มิฉะนั้นจะเล่นต่อไม่ได้</p>
        </div>
      </article>

      <!-- Zombie Attack -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-skull-crossbones mr-2"></i> การโจมตีซอมบี้
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">โจมตีซอมบี้ได้ยังไง?</span> ในอีเวนต์สำรวจ เลือกโจมตีซอมบี้ที่ปรากฏในแผนที่ ใช้พลังงาน 10 หน่วยต่อการโจมตี 1 ครั้ง</p>
          <div>
            <span class="text-indigo-400">ต้องใช้ทรัพยากรอะไรบ้าง?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>พลังงาน 10 หน่วยต่อการโจมตี</li>
            </ul>
          </div>
          <div>
            <span class="text-indigo-400">ผลลัพธ์ที่เป็นไปได้</span>
            <ul class="list-disc pl-5 mt-1">
              <li><strong>ซอมบี้ตาย:</strong> ได้รับเหรียญเงินและ EXP (ถ้าเลเวล น้อยกว่า 30) อาจเลเวลอัพ (ได้รับ 5 แต้มสเตตัสต่อเลเวล) อีเวนต์ดำเนินต่อด้วยซอมบี้ตัวต่อไปหรือสิ้นสุดหากไม่มีซอมบี้เหลือ</li>
              <li><strong>ผู้เล่นตาย:</strong> หากพลังชีวิตหมดหรือระดับเชื้อโรคถึง 100 สร้างอีเวนต์ "เกิดใหม่" โดยจะสูญเสียไอเทม(เสีย 0-1), ทักษะ(เสีย 0), รอยสัก(เสีย 0-1) และห้องจำนวน(เสีย 0-1)</li>
            </ul>
          </div>
          <p><span class="text-indigo-400">ข้อจำกัด?</span> ต้องมีพลังงาน 10 หน่วย, อยู่ในอีเวนต์ที่ยังไม่หมดเวลา, และมีซอมบี้ให้โจมตี</p>
        </div>
      </article>

      <!-- Room System -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-skull-crossbones mr-2"></i> ระบบห้อง
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">ระบบห้องทำงานอย่างไร?</span> ระบบห้องสำคัญมาก! ผู้เล่นจะต้องเคลื่อนย้ายตัวละครเพื่อหลบการลอบสังหารจากผู้เล่นคนอื่นที่อาจมาลอบโจมตีคุณ</p>
          <div>
            <span class="text-indigo-400">ตัวละครย้ายอัตโนมัติ?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>ทุกครั้งที่มีผู้เล่นพยายามลอบสังหารคุณ ตัวละครของคุณจะย้ายห้องอัตโนมัติ ไม่ว่าพวกเขาจะเจอคุณหรือไม่</li>
            </ul>
          </div>
        </div>
      </article>
    </section>

    <!-- Assassination and Trap System -->
    <section class="space-y-6 mt-6">
      <h2 class="text-lg font-semibold text-indigo-200 mb-4">
        <i class="fas fa-crosshairs mr-2"></i> ระบบลอบสังหารและกัปดัก
      </h2>

     <!-- Find Random Survivor -->
<article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
  <h3 class="text-base font-semibold text-indigo-200 mb-2">
    <i class="fas fa-search mr-2"></i> การค้นหาผู้รอดชีวิต
  </h3>

  <div class="text-indigo-200 text-sm space-y-3">
    <!-- What is it -->
    <p>
      <span class="text-indigo-400">การค้นหาผู้รอดชีวิตคืออะไร?</span>
      คือการสุ่มหาเป้าหมาย (ผู้เล่นอื่น) ที่ <em>สามารถลอบสังหารได้</em> โดยระบบจะสุ่มจากผู้เล่นที่มีจำนวนห้องใกล้เคียงกับคุณ
      หากพบเป้าหมาย ระบบจะตั้งค่าเป้าหมายให้กับคุณอัตโนมัติ
    </p>

    <!-- Requirements & Cost -->
    <div>
      <span class="text-indigo-400">เงื่อนไขและค่าใช้จ่าย</span>
      <ul class="list-disc pl-5 mt-1 space-y-1">
        <li>ต้องมีอย่างน้อย <strong>5 ห้อง</strong> จึงจะค้นหาได้</li>
        <li>ตัวละครของคุณต้อง <strong>มีชีวิต</strong></li>
        <li class="mt-1"><strong>ค่าใช้จ่าย</strong> (ชำระเฉพาะเมื่อ “พบ” เป้าหมายเท่านั้น):
          <ul class="list-disc pl-5 mt-1">
            <li>ถ้าคุณมี <strong>5 ห้อง</strong>: ใช้ <strong>Silver 500</strong></li>
            <li>ถ้าคุณมี <strong>6–10 ห้อง</strong>:
              เลือกชำระ <strong>Diamond</strong> หรือ <strong>Gold</strong> ตามตาราง
              (ต้องระบุ <code>ประเภทหน่วนเงิน</code> เป็น <code>diamond</code> หรือ <code>gold</code>)
              <div class="mt-2 overflow-x-auto">
                <table class="min-w-full text-left text-xs border border-gray-700">
                  <thead class="bg-gray-800 text-indigo-300">
                    <tr>
                      <th class="px-3 py-2 border-b border-gray-700">จำนวนห้องของคุณ</th>
                      <th class="px-3 py-2 border-b border-gray-700">Diamond</th>
                      <th class="px-3 py-2 border-b border-gray-700">Gold</th>
                    </tr>
                  </thead>
                  <tbody class="text-indigo-200">
                    <tr><td class="px-3 py-2 border-b border-gray-700">6</td><td class="px-3 py-2 border-b border-gray-700">3</td><td class="px-3 py-2 border-b border-gray-700">50</td></tr>
                    <tr><td class="px-3 py-2 border-b border-gray-700">7</td><td class="px-3 py-2 border-b border-gray-700">6</td><td class="px-3 py-2 border-b border-gray-700">75</td></tr>
                    <tr><td class="px-3 py-2 border-b border-gray-700">8</td><td class="px-3 py-2 border-b border-gray-700">9</td><td class="px-3 py-2 border-b border-gray-700">100</td></tr>
                    <tr><td class="px-3 py-2 border-b border-gray-700">9</td><td class="px-3 py-2 border-b border-gray-700">12</td><td class="px-3 py-2 border-b border-gray-700">125</td></tr>
                    <tr><td class="px-3 py-2">10</td><td class="px-3 py-2">15</td><td class="px-3 py-2">150</td></tr>
                  </tbody>
                </table>
              </div>
            </li>
          </ul>
        </li>
        <li class="text-yellow-300">หาก <strong>ไม่พบ</strong> เป้าหมาย <u>จะไม่ถูกหักเงิน</u></li>
      </ul>
    </div>

    <!-- How it works -->
    <div>
      <span class="text-indigo-400">ขั้นตอนการค้นหา</span>
      <ul class="list-disc pl-5 mt-1 space-y-1">
        <li>กดปุ่ม <strong>ค้นหาผู้รอดชีวิต</strong> ในเกม</li>
        <li>ถ้าคุณมี 6–10 ห้อง ระบบจะใช้รูปแบบชำระเงินตามที่คุณเลือก (<code>diamond</code> หรือ <code>gold</code>)</li>
        <li>ระบบจะสุ่มผู้เล่นจากช่วงห้องที่กำหนด (ดูหัวข้อถัดไป)</li>
        <li>ถ้าพบเป้าหมาย: ระบบตั้งค่าเป้าหมายทันที และหักทรัพยากรตามตาราง</li>
        <li>ถ้าไม่พบ: ระบบจะแจ้งว่าไม่พบเป้าหมาย และไม่หักทรัพยากร</li>
      </ul>
    </div>

    <!-- Search Ranges -->
    <div>
      <span class="text-indigo-400">ช่วงห้องที่ใช้ในการค้นหา</span>
      <ul class="list-disc pl-5 mt-1">
        <li>คุณมี <strong>5 ห้อง</strong>: ค้นหาในห้อง <strong>5</strong></li>
        <li>คุณมี <strong>6 ห้อง</strong>: ค้นหาในห้อง <strong>6–7-8-9-10</strong></li>
        <li>คุณมี <strong>7 ห้อง</strong>: ค้นหาในห้อง <strong>6–7-8-9-10</strong></li>
        <li>คุณมี <strong>8 ห้อง</strong>: ค้นหาในห้อง <strong>6–7-8-9-10</strong></li>
        <li>คุณมี <strong>9 ห้อง</strong>: ค้นหาในห้อง <strong>6–7-8-9-10</strong></li>
        <li>คุณมี <strong>10 ห้อง</strong>: ค้นหาในห้อง <strong>6–7-8-9-10</strong></li>
      </ul>
      <p class="text-xs text-indigo-300 mt-1">*ถ้าไม่เข้ากับตารางด้านบน ระบบจะสร้างช่วงทั่วไป ±2 ห้องรอบตัวคุณ</p>
    </div>

    <!-- Target filtering -->
    <div>
      <span class="text-indigo-400">กฎคัดกรองเป้าหมาย</span>
      <ul class="list-disc pl-5 mt-1 space-y-1">
        <li>ไม่สุ่มตัวเอง และ <strong>ไม่สุ่มซ้ำเป้าหมายเดิม</strong> ของคุณ</li>
        <li>เป้าหมายต้อง <strong>มีชีวิต</strong> และอยู่ใน <strong>ช่วงห้อง</strong> ที่กำหนด</li>
        <li>จำกัดต่อเป้าหมาย: <strong>ไม่เกิน 6 ครั้ง/วัน</strong> จากผู้เล่นทั้งหมด (ถ้าเป้าหมายโดนถึงโควตา จะถูกข้าม)</li>
      </ul>
    </div>

    <!-- Results -->
    <div>
      <span class="text-indigo-400">ผลลัพธ์ที่เป็นไปได้</span>
      <ul class="list-disc pl-5 mt-1 space-y-1">
        <li><strong class="text-red-400">ไม่พบเป้าหมาย:</strong> ไม่มีการตั้งเป้าหมาย และ <u>ไม่หักทรัพยากร</u></li>
      </ul>
    </div>

    <!-- Tips -->
    <div>
      <span class="text-indigo-400">ทิปส์</span>
      <ul class="list-disc pl-5 mt-1 space-y-1">
        <li>ถ้าคุณมี 6–10 ห้อง อย่าลืมเลือกประเภทการชำระเงินให้ถูกต้องก่อนเริ่มค้นหา</li>
        <li>ถ้าพื้นที่ห้องว่างมากหรือเป้าหมายถึงโควตาประจำวัน อาจค้นหาไม่เจอ—ลองใหม่ภายหลัง</li>
      </ul>
    </div>
  </div>
</article>


<!-- Assassination -->
<article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
  <h3 class="text-base font-semibold text-indigo-200 mb-2">
    <i class="fas fa-skull-crossbones mr-2"></i> การลอบสังหาร
  </h3>

  <div class="text-indigo-200 text-sm space-y-3">
    <!-- What is it -->
    <p>
      <span class="text-indigo-400">การลอบสังหารคืออะไร?</span>
      การโจมตีผู้เล่นอื่น (ผู้รอดชีวิต) เพื่อขโมยทรัพยากร เช่น เพชรและทอง โดยต้องเดาว่าพวกเขาซ่อนอยู่ในห้องใด
    </p>

    <!-- Cost -->
    <div>
      <span class="text-indigo-400">ต้องใช้ทรัพยากรอะไรบ้าง?</span>
      <ul class="list-disc pl-5 mt-1 space-y-1">
        <li>ลูกเต๋า:
          <ul class="list-disc pl-5 mt-1 space-y-1">
            <li>3 ลูกเต๋า</li>
            <li>4 ลูกเต๋า : ใช้ 1 ไอเทมช่วยลอบสังหาร</li>
            <li>5 ลูกเต๋า : ใช้ 3 ไอเทมช่วยลอบสังหาร</li>
            <li>6 ลูกเต๋า : ใช้ 5 ไอเทมช่วยลอบสังหาร</li>
          </ul>
        </li>
      </ul>
    </div>

    <!-- Buildings / Search rules -->
    <div>
      <span class="text-indigo-400">โหมดตึก & การค้นหา</span>
      <ul class="list-disc pl-5 mt-1 space-y-1">
        <li><strong>ตึก 1 และตึก 2</strong>: ปิดชื่อ/ทรัพยากร (บุคคลลึกลับ), เสีย silver ในการค้นหา, ค้นหาได้วันละ <strong>10</strong> ครั้ง, ต้องลอบสังหารให้ครบ <strong>3</strong> ครั้งก่อนค้นหาใหม่, ลอบสังหารสำเร็จได้ <strong>ค้นหาฟรี +1</strong>, ถ้าถูกลอบสังหารครบ <strong>6</strong> ครั้งในวันเดียวจะถูกค้นหาไม่เจอ (ยกเว้นมีคิวค้นหาค้าง)</li>
        <li><strong>ตึก 2</strong>: ปิดสร้างตัวละครใหม่ชั่วคราว ตัวละครที่มีอยู่แล้วใช้กฎเดียวกับตึก 1</li>
        <li><strong>เงื่อนไขจำนวนห้อง</strong>: ผู้เล่น <strong>5 ห้อง</strong> ค้นหาได้เฉพาะผู้เล่น <strong>5 ห้อง</strong> / ผู้เล่น <strong>6–10 ห้อง</strong> ค้นหากันเจอทั้งหมด</li>
      </ul>
    </div>

    <!-- Steps -->
    <div>
      <span class="text-indigo-400">ขั้นตอนการลอบสังหาร?</span>
      <ul class="list-disc pl-5 mt-1 space-y-1">
        <li><strong>เลือกห้อง:</strong> ถ้าเดาห้องผิด ➜ ล้มเหลวทันที (ไม่มีการต่อสู้)</li>
        <li><strong>กัปดัก:</strong> ถ้าเป้าหมายมีกัปดัก คุณจะได้รับดาเมจทันที และเป้าหมายได้ลูกเต๋าเพิ่มตามกัปดัก
          <ul class="list-disc pl-5 mt-1">
            <li>อุปกรณ์ป้องกันบางชนิดช่วยกันดาเมจ เช่น <strong>หน้ากากกันแก๊ส</strong> กันแก๊สพิษ, <strong>หน้ากากเนฟรอน</strong> กันแก๊สพิษ/ไวรัส</li>
            <li>กัปดักมีจำนวนครั้งใช้งาน เมื่อเหลือ 0 จะปิดการทำงาน</li>
          </ul>
        </li>
        <li><strong>ทอยลูกเต๋า:</strong> ทั้งสองฝ่ายทอยลูกเต๋า ผลต่างกำหนดจำนวนครั้งที่โจมตี
          <ul class="list-disc pl-5 mt-1">
            <li>หากผลต่าง &gt; 0 ➜ ผู้ลอบสังหารโจมตีตามจำนวนผลต่าง</li>
            <li>หากผลต่าง &lt; 0 ➜ เป้าหมายโจมตีตามจำนวนผลต่าง (ค่าเป็นบวก)</li>
            <li>บางสกิลของเป้าหมายเช่น <em>หนังสือวิญญาณคุ้มกัน</em> จะให้ลูกเต๋า +1</li>
          </ul>
        </li>
      </ul>
    </div>

    <!-- NEW: Resource loss rules -->
    <div>
      <span class="text-indigo-400">การยึดเพชร/ทอง ขึ้นกับ “ส่วนต่างจำนวนห้อง”</span>
      <p class="mt-1">
        ตั้งแต่ <strong>20/09/2568</strong> การยึดเพชร/ทองเมื่อฝ่ายหนึ่ง “ตาย” จะไม่ใช่ 50% คงที่อีกต่อไป แต่จะขึ้นกับ
        <strong>ความต่างของจำนวนห้อง</strong> ระหว่างผู้ลอบสังหารกับเป้าหมาย (ทั้งตอนที่คุณฆ่าเขา และตอนที่คุณถูกเขาฆ่า)
        และยังคงหัก <strong>ภาษีเซิร์ฟเวอร์ 30%</strong> จากจำนวนที่ถูกยึดก่อนส่งให้ผู้ชนะ
      </p>
      <div class="mt-2 overflow-x-auto">
        <table class="w-full text-left text-xs border border-gray-700">
          <thead class="bg-gray-800">
            <tr>
              <th class="px-2 py-1 border-b border-gray-700">ส่วนต่างจำนวนห้อง</th>
              <th class="px-2 py-1 border-b border-gray-700">ยึด (ก่อนหักภาษี)</th>
              <th class="px-2 py-1 border-b border-gray-700">ภาษีเซิร์ฟเวอร์</th>
              <th class="px-2 py-1 border-b border-gray-700">อัตรา</th>
            </tr>
          </thead>
          <tbody>
            <tr><td class="px-2 py-1 border-b border-gray-800">เท่ากัน (0)</td><td class="px-2 py-1 border-b border-gray-800"><strong>50%</strong></td><td class="px-2 py-1 border-b border-gray-800">30% ของที่ยึด</td><td class="px-2 py-1 border-b border-gray-800">สูง</td></tr>
            <tr><td class="px-2 py-1 border-b border-gray-800">ห่าง 1 ห้อง</td><td class="px-2 py-1 border-b border-gray-800"><strong>40%</strong></td><td class="px-2 py-1 border-b border-gray-800">30% ของที่ยึด</td><td class="px-2 py-1 border-b border-gray-800">กลางสูง</td></tr>
            <tr><td class="px-2 py-1 border-b border-gray-800">ห่าง 2 ห้อง</td><td class="px-2 py-1 border-b border-gray-800"><strong>30%</strong></td><td class="px-2 py-1 border-b border-gray-800">30% ของที่ยึด</td><td class="px-2 py-1 border-b border-gray-800">กลาง</td></tr>
            <tr><td class="px-2 py-1 border-b border-gray-800">ห่าง 3 ห้อง</td><td class="px-2 py-1 border-b border-gray-800"><strong>20%</strong></td><td class="px-2 py-1 border-b border-gray-800">30% ของที่ยึด</td><td class="px-2 py-1 border-b border-gray-800">กลางต่ำ</td></tr>
            <tr><td class="px-2 py-1 border-b border-gray-800">ห่าง 4 ห้อง</td><td class="px-2 py-1 border-b border-gray-800"><strong>10%</strong></td><td class="px-2 py-1 border-b border-gray-800">30% ของที่ยึด</td><td class="px-2 py-1 border-b border-gray-800">ต่ำ</td></tr>
            <tr><td class="px-2 py-1">ห่าง ≥ 5 ห้อง</td><td class="px-2 py-1"><strong>0%</strong></td><td class="px-2 py-1">—</td><td class="px-2 py-1">ไม่ยึด</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Outcomes -->
    <div>
      <span class="text-indigo-400">ผลลัพธ์ที่เป็นไปได้?</span>
      <ul class="list-disc pl-5 mt-1 space-y-1">
        <li>
          <strong>สำเร็จ (คุณฆ่าเป้าหมาย):</strong>
          ได้รับ <strong>เพชร/ทองตามตารางอัตรา</strong> ด้านบน (หัก <strong>ภาษีเซิร์ฟเวอร์ 30%</strong> จากส่วนที่ยึด) เป้าหมาย “ตาย” และมีโอกาส <strong>สูญเสียไอเทม</strong> ตามตารางด้านล่าง
          <div class="mt-2">
            <span class="text-indigo-400">การสูญเสียไอเทมเมื่อ “ตาย”</span>
            <div class="mt-1 overflow-x-auto">
              <table class="w-full text-left text-xs border border-gray-700">
                <thead class="bg-gray-800">
                  <tr>
                    <th class="px-2 py-1 border-b border-gray-700">ส่วนต่างจำนวนห้อง</th>
                    <th class="px-2 py-1 border-b border-gray-700">โอกาสเสียไอเทม 1 ชิ้น</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td class="px-2 py-1 border-b border-gray-800">เท่ากัน (0)</td><td class="px-2 py-1 border-b border-gray-800"><strong>50%</strong></td></tr>
                  <tr><td class="px-2 py-1 border-b border-gray-800">ห่าง 1 ห้อง</td><td class="px-2 py-1 border-b border-gray-800"><strong>40%</strong></td></tr>
                  <tr><td class="px-2 py-1 border-b border-gray-800">ห่าง 2 ห้อง</td><td class="px-2 py-1 border-b border-gray-800"><strong>30%</strong></td></tr>
                  <tr><td class="px-2 py-1 border-b border-gray-800">ห่าง 3 ห้อง</td><td class="px-2 py-1 border-b border-gray-800"><strong>20%</strong></td></tr>
                  <tr><td class="px-2 py-1 border-b border-gray-800">ห่าง 4 ห้อง</td><td class="px-2 py-1 border-b border-gray-800"><strong>10%</strong></td></tr>
                  <tr><td class="px-2 py-1">ห่าง ≥ 5 ห้อง</td><td class="px-2 py-1"><strong>0%</strong></td></tr>
                </tbody>
              </table>
            </div>
            <ul class="list-disc pl-5 mt-2 space-y-1">
              <li>เช็คแยกหมวดแบบอิสระ: <strong>สวมใส่/ติดตัว</strong> สูงสุด 1 ชิ้น, <strong>รอยสัก</strong> สูงสุด 1 ลาย, <strong>ของในกระเป๋า</strong> สูงสุด 1 ชิ้น</li>
              <li><strong>สกิล</strong> ไม่สูญเสีย (0 เสมอ)</li>
              <li>อาจเสียจำนวนห้องแบบสุ่ม <strong>0–1 ห้อง</strong></li>
              <li>เมื่อเป้าหมายกดยอมรับบทลงโทษ ไอเทมที่ถูกยึดจะส่งเข้า <strong>กล่องปริศนา</strong> ของผู้ชนะ</li>
            </ul>
          </div>
        </li>
        <li>
          <strong>ล้มเหลว (คุณตาย/ไม่เจอเป้าหมาย):</strong>
          <ul class="list-disc pl-5 mt-1 space-y-1">
            <li>ถ้า “เดาผิดห้อง” ➜ จบการลอบสังหารทันที <strong>ไม่มีการต่อสู้/ไม่มีของหาย</strong></li>
            <li>ถ้าคุณ “ตาย” จากกับดักหรือการต่อสู้ ➜ โดนยึด <strong>เพชร/ทองตามตารางอัตรา</strong> (หักภาษี 30%) และเช็ค <strong>การสูญเสียไอเทม</strong> ตามตารางเดียวกัน</li>
          </ul>
        </li>
      </ul>
    </div>

    <!-- Example -->
    <div>
      <span class="text-indigo-400">ตัวอย่างคำนวณ (อีกฝ่ายมี 100 เพชร และ 100 ทอง)</span>
      <p class="mt-1">คำนวณเหมือนกันทั้งเพชรและทอง — ตารางด้านล่างแสดง “ยึดก่อนภาษี / ภาษี (30%) / ได้รับสุทธิ / เหลือของเหยื่อ”</p>
      <div class="mt-2 overflow-x-auto">
        <table class="w-full text-left text-xs border border-gray-700">
          <thead class="bg-gray-800">
            <tr>
              <th class="px-2 py-1 border-b border-gray-700">ส่วนต่างห้อง</th>
              <th class="px-2 py-1 border-b border-gray-700">ยึด</th>
              <th class="px-2 py-1 border-b border-gray-700">ภาษี</th>
              <th class="px-2 py-1 border-b border-gray-700">ผู้ชนะได้สุทธิ</th>
              <th class="px-2 py-1 border-b border-gray-700">เหยื่อเหลือ</th>
            </tr>
          </thead>
          <tbody>
            <tr><td class="px-2 py-1 border-b border-gray-800">0 ห้อง (50%)</td><td class="px-2 py-1 border-b border-gray-800">50</td><td class="px-2 py-1 border-b border-gray-800">15</td><td class="px-2 py-1 border-b border-gray-800">35</td><td class="px-2 py-1 border-b border-gray-800">50</td></tr>
            <tr><td class="px-2 py-1 border-b border-gray-800">1 ห้อง (40%)</td><td class="px-2 py-1 border-b border-gray-800">40</td><td class="px-2 py-1 border-b border-gray-800">12</td><td class="px-2 py-1 border-b border-gray-800">28</td><td class="px-2 py-1 border-b border-gray-800">60</td></tr>
            <tr><td class="px-2 py-1 border-b border-gray-800">2 ห้อง (30%)</td><td class="px-2 py-1 border-b border-gray-800">30</td><td class="px-2 py-1 border-b border-gray-800">9</td><td class="px-2 py-1 border-b border-gray-800">21</td><td class="px-2 py-1 border-b border-gray-800">70</td></tr>
            <tr><td class="px-2 py-1 border-b border-gray-800">3 ห้อง (20%)</td><td class="px-2 py-1 border-b border-gray-800">20</td><td class="px-2 py-1 border-b border-gray-800">6</td><td class="px-2 py-1 border-b border-gray-800">14</td><td class="px-2 py-1 border-b border-gray-800">80</td></tr>
            <tr><td class="px-2 py-1 border-b border-gray-800">4 ห้อง (10%)</td><td class="px-2 py-1 border-b border-gray-800">10</td><td class="px-2 py-1 border-b border-gray-800">3</td><td class="px-2 py-1 border-b border-gray-800">7</td><td class="px-2 py-1 border-b border-gray-800">90</td></tr>
            <tr><td class="px-2 py-1">≥ 5 ห้อง (0%)</td><td class="px-2 py-1">0</td><td class="px-2 py-1">0</td><td class="px-2 py-1">0</td><td class="px-2 py-1">100</td></tr>
          </tbody>
        </table>
      </div>
      <p class="mt-2 text-gray-300">
        ตัวอย่าง: ต่างกัน <strong>2 ห้อง</strong> ➜ ยึด <strong>30</strong>, ภาษี <strong>9</strong>, ผู้ชนะได้สุทธิ <strong>21</strong>, เหยื่อเหลือ <strong>70</strong> (ทั้งเพชรและทอง)
      </p>
    </div>

    <!-- Requirements / Limits -->
    <div>
      <span class="text-indigo-400">ข้อจำกัด</span>
      <ul class="list-disc pl-5 mt-1 space-y-1">
        <li>ทั้งสองฝ่ายต้องมีชีวิตอยู่ และผู้ลอบสังหารต้อง <strong>ไม่อยู่ในอีเวนต์</strong></li>
        <li>ต้องอยู่ <strong>ตึกเดียวกัน</strong> จึงจะลอบสังหารได้</li>
        <li>มี <strong>โล่กันลอบสังหาร</strong> (ช่วงคุ้มกัน) จะถูกลอบสังหารไม่ได้</li>
        <li>ผู้ลอบสังหารคนเดิมโจมตีเป้าหมายคนเดิมได้วันละ <strong>สูงสุด 6 ครั้ง</strong> และสะสมรวมสูงสุด <strong>3 ครั้ง</strong> ต่อเป้าหมายก่อนที่ระบบจะรีเซ็ต/ปิดเป้า</li>
      </ul>
    </div>

  </div>
</article>



      <!-- Trap Installation -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-bolt mr-2"></i> การติดตั้งกัปดัก
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">กัปดักคืออะไร?</span> อุปกรณ์ป้องกันที่ทำดาเมจและเพิ่มจำนวนลูกเต๋าเมื่อถูกผู้เล่นอื่นลอบสังหาร</p>
          <p><span class="text-indigo-400">ติดตั้งกัปดักได้ยังไง?</span> คราฟไอเทมกัปดักได้ที่ห้องคราฟ</p>
          <div>
            <span class="text-indigo-400">กัปดักทำงานยังไง?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>เพิ่มลูกเต๋าให้คุณ 1-4 ลูก ในการต่อสู้</li>
              <li>กัปดักจะถูกใช้และหายไปหลังจากทำงาน</li>
            </ul>
          </div>
        </div>
      </article>
    </section>

    <!-- Building System -->
    <section class="space-y-6 mt-6">
      <h2 class="text-lg font-semibold text-indigo-200 mb-4">
        <i class="fas fa-building mr-2"></i> ระบบตึก และประกันชีวิต
      </h2>

      <!-- Building Differences -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-exchange-alt mr-2"></i> ความแตกต่างระหว่างตึก 1 และตึก 2
        </h3>
        <div class="text-indigo-200 text-sm space-y-3">
          <p>
            <span class="text-indigo-400">ตึกคืออะไร?</span>
            ผู้เล่นจะอาศัยอยู่ในตึก 1 หรือตึก 2 โดยเริ่มต้นจะอยู่ตึก 1 เมื่อครบเงื่อนไขสามารถย้ายไปตึก 2 ได้ที่หน้า <strong>ผู้จัดการตึก</strong>
          </p>

          <div>
            <span class="text-indigo-400">เงื่อนไขการย้ายตึก</span>
            <ul class="list-disc pl-5 mt-1 space-y-1">
              <li>ต้องมีห้องครบ <strong>12 ห้อง</strong></li>
              <li>คูลดาวน์การย้ายตึก <strong>3 วัน</strong> ต่อครั้ง</li>
              <li>ต้องติ๊กยอมรับความเสี่ยงก่อนย้ายไปตึก 2</li>
            </ul>
          </div>

          <div>
            <span class="text-indigo-400">ตึก 1 (มาตรฐาน)</span>
            <ul class="list-disc pl-5 mt-1 space-y-1">
              <li>เมื่อตายจาก PvP/ระเบิด/วิญญาณเฝ้าห้อง การสูญเสียไอเทมจะคำนวณตาม <strong>ความห่างของจำนวนห้อง</strong> ระหว่างผู้เล่นที่ตายกับฝ่ายตรงข้าม</li>
              <li>โทษตายเบากว่าตึก 2 (โอกาส/จำนวนสูญเสียต่ำกว่า)</li>
              <li>สามารถสร้างตัวละครใหม่ได้ตามปกติ</li>
            </ul>
          </div>

          <div>
            <span class="text-indigo-400 text-red-300">ตึก 2 (โหมดเสี่ยง)</span>
            <ul class="list-disc pl-5 mt-1 space-y-1">
              <li>ชื่อและข้อมูลผู้เล่นจะถูกปิด เหมือนตึก 1</li>
              <li><strong class="text-red-300">ไม่ใช้</strong> ความห่างของห้องในการคำนวณไอเทมที่จะเสีย</li>
              <li>เมื่อตายจากการลอบสังหาร / ถูกวางระเบิด / ถูกวิญญาณเฝ้าห้องฆ่า จะสูญเสียทรัพยากรแบบคงที่ตามช่วงจำนวน:
                <ul class="list-disc pl-5 mt-1">
                  <li>ไอเทมที่ใส่อยู่ <strong>2–3 ชิ้น</strong></li>
                  <li>ไอเทมในกระเป๋า <strong>2–3 ชิ้น</strong></li>
                  <li>ทักษะ <strong>2–3 ชิ้น</strong></li>
                  <li>รอยสัก <strong>2–3 ลาย</strong></li>
                  <li>ห้อง <strong>0–1 ห้อง</strong></li>
                </ul>
              </li>
              <li class="text-yellow-300">ปิดการสร้างตัวละครใหม่ชั่วคราวสำหรับตึก 2</li>
            </ul>
          </div>

          <div class="bg-red-900 bg-opacity-20 border border-red-500 border-opacity-50 rounded-lg p-3 mt-2">
            <p class="text-red-300 text-xs">
              <i class="fas fa-exclamation-triangle mr-1"></i>
              <strong>สำคัญ:</strong> ตึก 2 ไม่สนใจว่าคุณอยู่ห้องใกล้หรือห่างจากคู่ต่อสู้ โทษตายจะถูกคำนวณแบบคงที่ตามตารางด้านบนเสมอ
            </p>
          </div>
        </div>
      </article>

      <!-- Insurance -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-shield-alt mr-2"></i> ระบบประกันชีวิต
        </h3>
        <div class="text-indigo-200 text-sm space-y-3">
          <p>
            <span class="text-indigo-400">ประกันคืออะไร?</span>
            ประกันชีวิตช่วยป้องกันการสูญเสียไอเทม สกิล และรอยสัก เมื่อคุณตายและเลือก "เกิดใหม่" หรือ "ยอมรับบทลงโทษ"
          </p>

          <div>
            <span class="text-indigo-400">ประกันป้องกันอะไร?</span>
            <ul class="list-disc pl-5 mt-1 space-y-1">
              <li>ไอเทมที่ใส่อยู่ <strong>ไม่หาย</strong></li>
              <li>ไอเทมในกระเป๋า <strong>ไม่หาย</strong></li>
              <li>สกิล <strong>ไม่หาย</strong></li>
              <li>รอยสัก <strong>ไม่หาย</strong></li>
            </ul>
          </div>

          <div>
            <span class="text-indigo-400">ประกันไม่ป้องกันอะไร?</span>
            <ul class="list-disc pl-5 mt-1 space-y-1">
              <li>การสูญเสียห้องยังคงมีผลตามปกติ</li>
              <li>การถูกยึดเพชร/ทองจากผู้เล่นฝ่ายตรงข้ามยังคงมีผล</li>
            </ul>
          </div>

          <p class="text-indigo-300 text-xs">
            <i class="fas fa-info-circle mr-1"></i>
            ประกันสามารถซื้อได้ที่หน้า <strong>ผู้จัดการตึก</strong> โดยจะมีอายุการใช้งานจำกัด
          </p>
        </div>
      </article>
    </section>

    <!-- Criminal Assassination System -->
    <section class="space-y-6 mt-6">
      <h2 class="text-lg font-semibold text-indigo-200 mb-4">
        <i class="fas fa-user-secret mr-2"></i> การลอบสังหารอาชญากร
      </h2>

      <!-- Criminal Assassination -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-skull-crossbones mr-2"></i> การลอบสังหารอาชญากร
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">การลอบสังหารอาชญากรคืออะไร?</span> การโจมตีตัวละคร NPC (อาชญากร) เพื่อรับรางวัล เช่น เหรียญเงิน, ชื่อเสียง, ทอง, และเพชร โดยต้องเดาว่าพวกเขาซ่อนอยู่ในห้องใด</p>
          <div>
            <span class="text-indigo-400">ต้องใช้ทรัพยากรอะไรบ้าง?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>พลังงาน 50 หน่วย</li>
              <li>เหรียญเงิน: 0 (1 ลูกเต๋า), 50 (2 ลูกเต๋า),  100 (3 ลูกเต๋า), หรือ 500 (4  ลูกเต๋า)</li>
            </ul>
          </div>
          <div>
            <span class="text-indigo-400">ขั้นตอนการลอบสังหาร?</span>
            <ul class="list-disc pl-5 mt-1">
              <li><strong>เลือกห้อง:</strong> ถ้าเดาห้องผิด จะล้มเหลวทันที</li>
              <li><strong>ทอยลูกเต๋า:</strong> คุณทอย 1-3 ลูกเต๋า อาชญากรทอย 1-4 ลูก ผลต่างกำหนดจำนวนครั้งที่โจมตี (ถ้าผลต่างเป็นบวก คุณโจมตี; ถ้าลบ อาชญากรโจมตี)</li>
            </ul>
          </div>
          <div>
            <span class="text-indigo-400">ผลลัพธ์ที่เป็นไปได้?</span>
            <ul class="list-disc pl-5 mt-1">
              <li><strong>สำเร็จ:</strong> ถ้าคุณฆ่าอาชญากรได้ คุณได้รับรางวัล (เหรียญเงิน, ชื่อเสียง, ทอง, เพชร) ตามที่กำหนด อาชญากรจะถูกลบออกจากระบบ</li>
              <li><strong>ล้มเหลว:</strong> ถ้าคุณตาย โดยจะสูญเสียไอเทม(เสีย 0-1), ทักษะ(เสีย 0), รอยสัก(เสีย 0-1) และห้องจำนวน(เสีย 0-1)</li>
              <li><strong>ไม่ตายทั้งคู่:</strong> อัปเดตความเสียหายที่ได้รับ และอาชญากรย้ายไปห้องใหม่</li>
            </ul>
          </div>
        </div>
      </article>
    </section>

    <!-- Item Decomposition System -->
    <section class="space-y-6 mt-6">
      <h2 class="text-lg font-semibold text-indigo-200 mb-4">
        <i class="fas fa-tools mr-2"></i> การแยกชิ้นส่วนไอเทม
      </h2>

      <!-- Item Decomposition -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-cogs mr-2"></i> การแยกชิ้นส่วนไอเทม
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">การแยกชิ้นส่วนไอเทมคืออะไร?</span> การทำลายไอเทมในกระเป๋าเพื่อรับไอเทมใหม่ตามรายการแยกชิ้นส่วน โดยขึ้นอยู่กับโอกาสที่กำหนด</p>
          <p><span class="text-indigo-400">แยกชิ้นส่วนได้ยังไง?</span> เลือกไอเทมที่ต้องการแยกในกระเป๋าและยืนยันการแยกชิ้นส่วน</p>
          <p><span class="text-indigo-400">ต้องใช้ทรัพยากรอะไรบ้าง?</span> ไม่ต้องใช้ทรัพยากร (เช่น พลังงาน, เหรียญเงิน) แต่ไอเทมเดิมจะหายไป</p>
          <div>
            <span class="text-indigo-400">ขั้นตอนการแยกชิ้นส่วน?</span>
            <ul class="list-disc pl-5 mt-1">
              <li><strong>เลือกไอเทม:</strong> ไอเทมต้องอยู่ในกระเป๋าและสามารถแยกชิ้นส่วนได้</li>
              <li><strong>ตรวจสอบโอกาส:</strong> ไอเทมใหม่แต่ละชิ้นมีเปอร์เซ็นต์โอกาส (เช่น 30%) ที่จะได้รับ</li>
              <li><strong>ผลลัพธ์:</strong> ไอเทมเดิมถูกลบ และอาจได้รับไอเทมใหม่ตามโอกาส</li>
            </ul>
          </div>
          <div>
            <span class="text-indigo-400">ผลลัพธ์ที่เป็นไปได้?</span>
            <ul class="list-disc pl-5 mt-1">
              <li><strong>สำเร็จและได้ไอเทม:</strong> ได้รับไอเทมใหม่ 1 ชิ้นหรือมากกว่าตามโอกาส ไอเทมใหม่เพิ่มในกระเป๋า</li>
              <li><strong>ล้มเหลว:</strong> ไอเทมเดิมถูกลบ แต่ไม่ได้รับไอเทมใหม่</li>
            </ul>
          </div>
        </div>
      </article>
    </section>
      <!-- Killer Robot System -->
    <section class="space-y-6 mt-6">
      <h2 class="text-lg font-semibold text-indigo-200 mb-4">
        <i class="fas fa-robot mr-2"></i> ระบบหุ่นยนต์สังหาร
      </h2>

      <!-- Killer Robot Overview -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-robot mr-2"></i> หุ่นยนต์สังหารคืออะไร?
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">หุ่นยนต์สังหารคืออะไร?</span> เป็นหุ่นยนต์ที่ผู้เล่นสามารถสร้างและส่งไปประจำแผนที่ต่างๆ เพื่อลอบโจมตีผู้เล่นคนอื่นที่มาสำรวจแผนที่นั้นๆ และค้นหาไอเทมให้เจ้าของ</p>
          <p><span class="text-indigo-400">สร้างและใช้งานยังไง?</span> คุณต้องคราฟหุ่นยนต์ในห้องคราฟ แล้วเลือกแผนที่ที่จะส่งหุ่นยนต์ไปประจำ เมื่อส่งไปแล้วจะไม่สามารถเรียกคืนได้ทันที สามารถส่งได้สูงสุด 2 ตัวต่อผู้เล่น</p>
          <p><span class="text-indigo-400">เงื่อนไขการส่งหุ่นยนต์?</span> หุ่นยนต์แต่ละตัวมีแผนที่ที่สามารถไปได้จำกัด เช่น หุ่นยนต์รัสต์แฮมเมอร์ไปได้เฉพาะรถไฟฟ้าใต้ดิน โรงพยาบาล หมู่บ้านคนรวย สวนสนุก โรงงานอุตสาหกรรม เท่านั้น</p>
          <div>
            <span class="text-indigo-400">อัปเกรดหุ่นยนต์ได้ยังไง?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>อัปเกรดด้วยไอเทม: ใช้ไอเทมที่กำหนดเพื่ออัปเกรด กระจาย 10 แต้มไปยัง stamina, str, int, agi (เพิ่ม maxHp, atk, critical, evasion) สามารถอัปเกรดได้ตาม maxUpgradeCount ของหุ่นยนต์</li>
              <li>อัปเกรดด้วยเงิน: ใช้ silver เพื่ออัปเกรด stats เพิ่มเติม เช่น เพิ่ม stamina +50, str +10, agi +10, int +10, defense +5 ตามระดับต่างๆ</li>
              <li>รีเซ็ต stats: ใช้ 1000 silver เพื่อรีเซ็ต stats จากการอัปเกรดด้วยไอเทม คืนแต้มกลับมาเพื่อกระจายใหม่</li>
              <li>ซ่อมแซมหุ่นยนต์: หากหุ่นยนต์ได้รับ damageTaken สามารถซ่อมด้วย 5000 silver</li>
            </ul>
          </div>
          <div>
            <span class="text-indigo-400">ค้นหาไอเทมยังไง?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>หลังจากส่งหุ่นยนต์ไปประจำแผนที่แล้ว 3 ชั่วโมง สามารถสั่งค้นหาไอเทมได้</li>
              <li>โบนัสอัตราการดรอปไอเทมเพิ่ม 10% ต่อชั่วโมงหลังจาก 3 ชั่วโมงแรก สูงสุด 100% (เช่น ครบ 4 ชม. +10%, ครบ 5 ชม. +20% เป็นต้น)</li>
              <li>ได้ไอเทมตาม rewards ของแผนที่นั้น รวมถึงโอกาสได้ diamond</li>
              <li>หลังค้นหาแล้ว ต้องรออีก 3 ชั่วโมงจึงเรียกหุ่นยนต์คืนได้</li>
            </ul>
          </div>
          <div>
            <span class="text-indigo-400">เรียกคืนหุ่นยนต์ยังไง?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>สามารถเรียกคืนได้หลังจากเวลาที่กำหนด (3 ชม. หลังจากสังไปสำรวจ หรือหลังค้นหาไอเทม)</li>
              <li>เมื่อเรียกคืน หุ่นยนต์จะกลับมาพร้อมไอเทมและ diamond ที่ค้นหาได้ (ถ้ามี)</li>
            </ul>
          </div>
          <div>
            <span class="text-indigo-400">ผู้เล่นเจอหุ่นยนต์ได้ยังไง?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>เมื่อกดกลับบ้านหลังจากสำรวจแผนที่สำเร็จ มีโอกาส 3% ที่จะเจอหุ่นยนต์ของผู้เล่นคนอื่น</li>
              <li>ถ้าเจอ จะเข้าต่อสู้กับหุ่นยนต์เหมือนต่อสู้ซอมบี้ (ไม่แสดง HP หุ่นยนต์และชื่อเจ้าของ)</li>
              <li>สามารถเลือกโจมตี (ใช้พลังงาน 10) หรือหนี (โอกาสตาม stats flee, ไม่ใช้พลังงาน)</li>
              <li>ถ้าหนีไม่สำเร็จ หุ่นยนต์จะโจมตีกลับ</li>
              <li>ถ้าฆ่าหุ่นยนต์ได้ จะได้ไอเทมจาก inventory ของหุ่นยนต์</li>
            </ul>
          </div>
          <div>
            <span class="text-indigo-400">ถ้าหุ่นยนต์ชนะจะได้อะไร?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>เจ้าของหุ่นยนต์จะได้รับ diamond/gold 50% จากผู้เล่นที่ถูกฆ่า (หักภาษี 30% ให้เซิร์ฟเวอร์)</li>
              <li>หุ่นยนต์ที่ไม่ตายจะยังคงประจำแผนที่นั้นต่อไป และยังสามารถค้นหาไอเทมได้</li>
            </ul>
          </div>
          <h3 class="text-base font-semibold text-indigo-200 mb-2">
            <i class="fas fa-map-marked-alt mr-2"></i> หุ่นยนต์แต่ละตัวสามารถไปแผนที่ไหนได้บ้าง?
          </h3>
          <div class="overflow-x-auto">
            <table class="min-w-full bg-gray-900 bg-opacity-80 rounded-md border border-gray-600 text-indigo-200 text-sm">
              <thead>
                <tr class="bg-gray-800">
                  <th class="px-4 py-2 text-left font-semibold">ชื่อหุ่นยนต์</th>
                  <th class="px-4 py-2 text-left font-semibold">แผนที่ที่สามารถไปได้</th>
                </tr>
              </thead>
              <tbody>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์รัสต์แฮมเมอร์</td>
                  <td class="px-4 py-2">รถไฟฟ้าใต้ดิน, โรงพยาบาล, หมู่บ้านคนรวย, สวนสนุก, โรงงานอุตสาหกรรม</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์ไคเนติก</td>
                  <td class="px-4 py-2">รถไฟฟ้าใต้ดิน, โรงพยาบาล, หมู่บ้านคนรวย, สวนสนุก, โรงงานอุตสาหกรรม, ห้างสรรพสินค้า, สถานีดับเพลิงร้าง, ค่ายทหาร</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์วอยด์</td>
                  <td class="px-4 py-2">โรงงานอุตสาหกรรม, ห้างสรรพสินค้า, สถานีดับเพลิงร้าง, ค่ายทหาร, สถาบันวิจัยหุ่นยนต์</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์รามศึก</td>
                  <td class="px-4 py-2">โรงงานอุตสาหกรรม, ห้างสรรพสินค้า, สถานีดับเพลิงร้าง, ค่ายทหาร, สถาบันวิจัยหุ่นยนต์, สถาบันวิจัยไวรัส, สุสานนักรบโบราณ, ตึกความมันคงแห่งชาติ</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์สุนัขบางแก้ว</td>
                  <td class="px-4 py-2">โรงงานอุตสาหกรรม, ห้างสรรพสินค้า, สถานีดับเพลิงร้าง, ค่ายทหาร, สถาบันวิจัยหุ่นยนต์, สถาบันวิจัยไวรัส, สุสานนักรบโบราณ, ตึกความมันคงแห่งชาติ</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์สุนัขหลังอาน</td>
                  <td class="px-4 py-2">โรงงานอุตสาหกรรม, ห้างสรรพสินค้า, สถานีดับเพลิงร้าง, ค่ายทหาร, สถาบันวิจัยหุ่นยนต์, สถาบันวิจัยไวรัส, สุสานนักรบโบราณ, ตึกความมันคงแห่งชาติ</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์หมีขั้วโลก</td>
                  <td class="px-4 py-2">โรงงานอุตสาหกรรม, ห้างสรรพสินค้า, สถานีดับเพลิงร้าง, ค่ายทหาร, สถาบันวิจัยหุ่นยนต์, สถาบันวิจัยไวรัส, สุสานนักรบโบราณ, ตึกความมันคงแห่งชาติ</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์พิทบูล</td>
                  <td class="px-4 py-2">โรงงานอุตสาหกรรม, ห้างสรรพสินค้า, สถานีดับเพลิงร้าง, ค่ายทหาร, สถาบันวิจัยหุ่นยนต์, สถาบันวิจัยไวรัส, สุสานนักรบโบราณ, ตึกความมันคงแห่งชาติ</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์กอริลล่า</td>
                  <td class="px-4 py-2">โรงงานอุตสาหกรรม, ห้างสรรพสินค้า, สถานีดับเพลิงร้าง, ค่ายทหาร, สถาบันวิจัยหุ่นยนต์, สถาบันวิจัยไวรัส, สุสานนักรบโบราณ, ตึกความมันคงแห่งชาติ</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์แอทลาส</td>
                  <td class="px-4 py-2">สถานีดับเพลิงร้าง, ค่ายทหาร, สถาบันวิจัยหุ่นยนต์, สถาบันวิจัยไวรัส, สุสานนักรบโบราณ, ตึกความมันคงแห่งชาติ</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์มังกรดิน</td>
                  <td class="px-4 py-2">สถานีดับเพลิงร้าง, ค่ายทหาร, สถาบันวิจัยหุ่นยนต์, สถาบันวิจัยไวรัส, สุสานนักรบโบราณ, ตึกความมันคงแห่งชาติ</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์มังกรไฟ</td>
                  <td class="px-4 py-2">สถานีดับเพลิงร้าง, ค่ายทหาร, สถาบันวิจัยหุ่นยนต์, สถาบันวิจัยไวรัส, สุสานนักรบโบราณ, ตึกความมันคงแห่งชาติ</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์จิ้งจอกเก้าหาง</td>
                  <td class="px-4 py-2">สถานีดับเพลิงร้าง, ค่ายทหาร, สถาบันวิจัยหุ่นยนต์, สถาบันวิจัยไวรัส, สุสานนักรบโบราณ, ตึกความมันคงแห่งชาติ</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์ซอมบี้</td>
                  <td class="px-4 py-2">สถานีดับเพลิงร้าง, ค่ายทหาร, สถาบันวิจัยหุ่นยนต์, สถาบันวิจัยไวรัส, สุสานนักรบโบราณ, ตึกความมันคงแห่งชาติ</td>
                </tr>
                <tr class="border-t border-gray-700">
                  <td class="px-4 py-2">หุ่นยนต์เสือดำ</td>
                  <td class="px-4 py-2">สถาบันวิจัยไวรัส, สุสานนักรบโบราณ, ตึกความมันคงแห่งชาติ, โรงเก็บศพจักรกล, ตึกปฏิบัติการพันธุกรรม</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </article>
      <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-percentage mr-2"></i> ตารางโบนัสดรอปตามเวลาที่รอ (หลังครบ 3 ชั่วโมง)
  </h2>

  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600">

    <div class="overflow-x-auto">
      <table class="min-w-full bg-gray-900 bg-opacity-80 rounded-md border border-gray-600 text-indigo-200 text-sm">
        <thead>
          <tr class="bg-gray-800">
            <th class="px-4 py-2 text-left font-semibold">เวลาที่ประจำ (ชั่วโมง)</th>
            <th class="px-4 py-2 text-right font-semibold">โบนัส</th>
            <th class="px-4 py-2 text-right font-semibold">แหวนงาช้าง (พื้นฐาน 0.25%)</th>
            <th class="px-4 py-2 text-right font-semibold">ถุงเหรียญเงิน (พื้นฐาน 1%)</th>
          </tr>
        </thead>
        <tbody>
          <tr class="border-t border-gray-700">
            <td class="px-4 py-2">3 (ขั้นต่ำค้นหา)</td>
            <td class="px-4 py-2 text-right">0%</td>
            <td class="px-4 py-2 text-right">0.250%</td>
            <td class="px-4 py-2 text-right">1.00%</td>
          </tr>
          <tr class="border-t border-gray-700">
            <td class="px-4 py-2">4</td>
            <td class="px-4 py-2 text-right">+10%</td>
            <td class="px-4 py-2 text-right">0.275%</td>
            <td class="px-4 py-2 text-right">1.10%</td>
          </tr>
          <tr class="border-t border-gray-700">
            <td class="px-4 py-2">5</td>
            <td class="px-4 py-2 text-right">+20%</td>
            <td class="px-4 py-2 text-right">0.300%</td>
            <td class="px-4 py-2 text-right">1.20%</td>
          </tr>
          <tr class="border-t border-gray-700">
            <td class="px-4 py-2">6</td>
            <td class="px-4 py-2 text-right">+30%</td>
            <td class="px-4 py-2 text-right">0.325%</td>
            <td class="px-4 py-2 text-right">1.30%</td>
          </tr>
          <tr class="border-t border-gray-700">
            <td class="px-4 py-2">7</td>
            <td class="px-4 py-2 text-right">+40%</td>
            <td class="px-4 py-2 text-right">0.350%</td>
            <td class="px-4 py-2 text-right">1.40%</td>
          </tr>
          <tr class="border-t border-gray-700">
            <td class="px-4 py-2">8</td>
            <td class="px-4 py-2 text-right">+50%</td>
            <td class="px-4 py-2 text-right">0.375%</td>
            <td class="px-4 py-2 text-right">1.50%</td>
          </tr>
          <tr class="border-t border-gray-700">
            <td class="px-4 py-2">9</td>
            <td class="px-4 py-2 text-right">+60%</td>
            <td class="px-4 py-2 text-right">0.400%</td>
            <td class="px-4 py-2 text-right">1.60%</td>
          </tr>
          <tr class="border-t border-gray-700">
            <td class="px-4 py-2">10</td>
            <td class="px-4 py-2 text-right">+70%</td>
            <td class="px-4 py-2 text-right">0.425%</td>
            <td class="px-4 py-2 text-right">1.70%</td>
          </tr>
          <tr class="border-t border-gray-700">
            <td class="px-4 py-2">11</td>
            <td class="px-4 py-2 text-right">+80%</td>
            <td class="px-4 py-2 text-right">0.450%</td>
            <td class="px-4 py-2 text-right">1.80%</td>
          </tr>
          <tr class="border-t border-gray-700">
            <td class="px-4 py-2">12</td>
            <td class="px-4 py-2 text-right">+90%</td>
            <td class="px-4 py-2 text-right">0.475%</td>
            <td class="px-4 py-2 text-right">1.90%</td>
          </tr>
          <tr class="border-t border-gray-700">
            <td class="px-4 py-2">≥13</td>
            <td class="px-4 py-2 text-right">+100% (เพดาน)</td>
            <td class="px-4 py-2 text-right font-semibold text-indigo-300">0.500%</td>
            <td class="px-4 py-2 text-right font-semibold text-indigo-300">2.00%</td>
          </tr>
        </tbody>
      </table>
    </div>

  </article>
      <section class="space-y-6 mt-6">
  
</section>

    </section>
    <section class="space-y-6 mt-6">
    <h2 class="text-lg font-semibold text-indigo-200 mb-4">
      <i class="fas fa-cogs mr-2"></i> ระบบอวัยวะจักรกล
    </h2>
    <!-- Overview -->
    <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
      <h3 class="text-base font-semibold text-indigo-200 mb-2">
        <i class="fas fa-info-circle mr-2"></i> ภาพรวมระบบอวัยวะจักรกล
      </h3>
      <div class="text-indigo-200 text-sm space-y-2">
        <p><span class="text-indigo-400">อวัยวะจักรกลคืออะไร?</span> อวัยวะจักรกลเป็นไอเทมพิเศษประเภท 'อวัยวะจักรกล' ที่ช่วยเพิ่มค่าพลัง (stats) ให้ตัวละคร เช่น stamina, str, int, agi, maxHp, atk, evasion, critical, และ defense โดยเปรียบเสมือนชิ้นส่วนหุ่นยนต์ที่ติดตั้งเพื่อยกระดับความสามารถของตัวละคร</p>
        <div>
          <span class="text-indigo-400">ชื่ออวัยวะจักรกลที่รองรับและทักษะที่ต้องใช้:</span>
          <ul class="list-disc pl-5 mt-1">
            <li><strong>ต้องใช้ทักษะ 'หนังสือวิธีติดตั้งอวัยวะจักรกล':</strong> ดวงตาจักรกล, สมองจักรกล, หัวใจจักรกล, ซี่โครงจักรกล, แขนจักรกล, หมัดจักรกล, ขาจักรกล</li>
            <li><strong>ต้องใช้ทักษะ 'หนังสือวิธีติดตั้งอวัยวะจักรกล2':</strong> ลำตัวจักรกล, สะโพกจักรกล, ตาหลังจักรกล, กรามจักรกล, ไหล่จักรกล, จมูกจักรกล, หูจักรกล</li>
            <li><strong>ต้องใช้ทักษะ 'หนังสือวิธีติดตั้งอวัยวะจักรกล3':</strong> ปีกจักรกล, หนวดจักรกล, เขาจักรกล</li>
          </ul>
        </div>
        <div>
          <span class="text-indigo-400">ข้อจำกัด:</span>
          <ul class="list-disc pl-5 mt-1">
            <li>ต้องมีทักษะที่เกี่ยวข้อง ('หนังสือวิธีติดตั้งอวัยวะจักรกล' หรือ 'หนังสือวิธีติดตั้งอวัยวะจักรกล2') จึงจะติดตั้งได้</li>
            <li>ตัวละครต้องอยู่ในสถานะ 'มีชีวิต' (isLive: true) และไม่อยู่ในเหตุการณ์ที่ไม่ใช่ประเภท 'trap'</li>
            <li>เลเวลของตัวละครต้องถึงระดับที่กำหนด (requiredLevel) ของอวัยวะจักรกล</li>
            <li>สามารถติดตั้งอวัยวะจักรกลได้เพียงชิ้นเดียวต่อชื่อ (เช่น ติดตั้ง 'ดวงตาจักรกล' ได้เพียงชิ้นเดียว)</li>
            <li>น้ำหนักของกระเป๋าต้องเพียงพอเมื่อถอนหรือสลับอวัยวะจักรกล</li>
          </ul>
        </div>
        <p><span class="text-indigo-400">การติดตั้งการ์ด:</span> อวัยวะจักรกลสามารถติดตั้งการ์ดประมวลผลเพื่อเพิ่มสเตตัสเพิ่มเติมได้ โดยการ์ดต้องเข้ากันได้กับชื่ออวัยวะจักรกล และไม่สามารถติดตั้งการ์ดที่มีชื่อซ้ำกันได้</p>
        <p><span class="text-indigo-400">การกระจายแต้ม:</span> ผู้เล่นสามารถกระจายแต้มสเตตัส (statPoints) เพื่อปรับแต่งความสามารถของอวัยวะจักรกล เช่น เพิ่ม stamina, str, int, หรือ agi ตามที่ต้องการ</p>
        <p><span class="text-indigo-400">การรีเซ็ต:</span> ใช้ 'การ์ดรีเซ็ตข้อมูล' เพื่อรีเซ็ตสเตตัสที่กระจายไป คืนแต้มทั้งหมดเพื่อกระจายใหม่ได้</p>
      </div>
    </article>
    <!-- Upgrade -->
    <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
      <h3 class="text-base font-semibold text-indigo-200 mb-2">
        <i class="fas fa-hammer mr-2"></i> การอัปเกรดอวัยวะจักรกล
      </h3>
      <div class="text-indigo-200 text-sm space-y-2">
        <div>
          <span class="text-indigo-400">เงื่อนไขการอัปเกรด:</span>
          <ul class="list-disc pl-5 mt-1">
            <li>ต้องใช้ 'การ์ดประมวลผลเสริม' และ/หรือ 'การ์ดประมวลผลเสริมขั้นกลาง' ตามระดับ rarity ปัจจุบันของอวัยวะจักรกล</li>
            <li>จำนวนการ์ดที่ต้องใช้และโอกาสสำเร็จขึ้นอยู่กับระดับ rarity</li>
            <li>ต้องกระจาย 3 แต้มสเตตัสในแต่ละครั้งที่อัปเกรด (เช่น stamina, str, int, agi)</li>
            <li>ตัวละครต้องอยู่ในสถานะ 'มีชีวิต' และไม่อยู่ในเหตุการณ์ที่ไม่ใช่ประเภท 'trap'</li>
          </ul>
        </div>
        <div>
          <span class="text-indigo-400">ตารางวัสดุและโอกาสสำเร็จ:</span>
          <div class="overflow-x-auto mt-2">
            <table class="w-full text-xs text-left text-indigo-200 border border-gray-600 rounded-md">
              <thead class="bg-gray-800">
                <tr>
                  <th class="px-3 py-2 border-b border-gray-600">Rarity</th>
                  <th class="px-3 py-2 border-b border-gray-600">โอกาสสำเร็จ</th>
                  <th class="px-3 py-2 border-b border-gray-600">วัสดุที่ต้องใช้</th>
                  <th class="px-3 py-2 border-b border-gray-600">ค่าใช้จ่ายป้องกัน (กันแตก)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rarity in rarityRequirements" :key="rarity.level">
                  <td class="px-3 py-2 border-b border-gray-600">{{ rarity.level }}</td>
                  <td class="px-3 py-2 border-b border-gray-600">{{ rarity.chance }}</td>
                  <td class="px-3 py-2 border-b border-gray-600">
                    <span v-for="(material, index) in rarity.materials" :key="index">
                      {{ material.itemName }} x{{ material.quantity }}{{ index < rarity.materials.length - 1 ? ', ' : '' }}
                    </span>
                  </td>
                  <td class="px-3 py-2 border-b border-gray-600">
                    {{ rarity.protectCost.diamond }} Diamond, {{ rarity.protectCost.silver }} Silver
                  </td>
                </tr>
                <tr>
                  <td class="px-3 py-2">20</td>
                  <td class="px-3 py-2" colspan="3">ระดับสูงสุด ไม่สามารถอัปเกรดต่อได้</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <span class="text-indigo-400">การป้องกันการสูญเสีย (กันแตก):</span>
          <ul class="list-disc pl-5 mt-1">
            <li>สามารถเลือกใช้ 'การป้องกัน' เพื่อป้องกันการสูญเสียอวัยวะจักรกลเมื่ออัปเกรดล้มเหลว</li>
            <li>ต้องใช้ diamond และ silver ตามระดับ rarity ดังตารางด้านบน</li>
            <li>หากไม่ใช้การป้องกันและอัปเกรดล้มเหลว อวัยวะจักรกลจะถูกทำลาย</li>
          </ul>
        </div>
        <div>
          <span class="text-indigo-400">ผลลัพธ์:</span>
          <ul class="list-disc pl-5 mt-1">
            <li><strong>สำเร็จ:</strong> เพิ่มสเตตัสตามที่เลือกกระจาย (3 แต้ม), เพิ่มระดับ rarity ขึ้น 1, และปรับสี rarity (rarityColors) ตามระดับใหม่ (เช่น rarity 1 เป็นสีเทา, rarity 10 เป็นสีเหลืองอำพัน)</li>
            <li><strong>ล้มเหลว:</strong> สูญเสียวัสดุที่ใช้ (การ์ดประมวลผล) และหากไม่ใช้การป้องกัน อวัยวะจักรกลจะถูกทำลาย</li>
            <li>ระดับ rarity สูงสุดคือ 20 เมื่อถึงระดับนี้จะไม่สามารถอัปเกรดต่อได้</li>
          </ul>
        </div>
        <div>
          <span class="text-indigo-400">หมายเหตุเพิ่มเติม:</span>
          <ul class="list-disc pl-5 mt-1">
            <li>การอัปเกรดจะบันทึกจำนวนครั้งที่สำเร็จ (mechanicalUpgrades) และจำนวนครั้งที่ล้มเหลว (failedMechanicalUpgrades) ในข้อมูลตัวละคร</li>
            <li>ทุกการอัปเกรดจะถูกบันทึกในระบบ log เพื่อติดตามประวัติการดำเนินการ</li>
            <li>การกระจายแต้มสเตตัสต้องอยู่ในขอบเขตของสเตตัสที่อัปเกรดได้ (upgradeableStats) ของอวัยวะจักรกล</li>
          </ul>
        </div>
      </div>
    </article>
  </section>
<section class="space-y-6 mt-6">
  <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-star mr-2"></i> ระบบโบนัสเซ็ต
  </h2>
  <!-- Set Bonus Overview -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-info-circle mr-2"></i> ภาพรวมระบบโบนัสเซ็ต
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ระบบโบนัสเซ็ตคืออะไร?</span> ระบบโบนัสเซ็ตเป็นกลไกที่ให้รางวัลโบนัสพิเศษเมื่อผู้เล่นสวมใส่ไอเทมครบตามจำนวนที่กำหนดในเซ็ตเดียวกัน เช่น เซ็ตทหาร, เซ็ตนักดับเพลิง, เซ็ตตัวตลก, หรือเซ็ตเทควันโด้ โดยโบนัสจะเพิ่มค่าพลัง เช่น maxHp, atk, critical, evasion, หรือ defense</p>
      <p><span class="text-indigo-400">วิธีรับโบนัสเซ็ต?</span> ผู้เล่นต้องสวมใส่ไอเทมที่อยู่ในเซ็ตเดียวกันตามจำนวนที่กำหนด (เช่น 4 หรือ 8 ชิ้นสำหรับเซ็ตทหาร) โดยไอเทมจะต้องอยู่ในสถานะสวมใส่และเป็นของเซ็ตที่กำหนด</p>
      <p><span class="text-indigo-400">การตรวจสอบโบนัสเซ็ต?</span> ผู้เล่นสามารถดูสถานะโบนัสเซ็ตได้ในแท็บ "โบนัสจากเซ็ต" ในหน้าต่างไอเทมที่สวมใส่ ซึ่งจะแสดงจำนวนไอเทมที่สวมใส่, โบนัสปัจจุบัน, และโบนัสในระดับถัดไป</p>
    </div>
  </article>
  <!-- Set Details -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-list mr-2"></i> รายละเอียดเซ็ตและโบนัส
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">รายการเซ็ตและโบนัส:</span></p>
      <div class="overflow-x-auto">
        <table class="min-w-full text-left text-xs border border-gray-600 rounded-md">
          <thead class="bg-gray-800">
            <tr>
              <th class="px-3 py-2 border-b border-gray-600">ชื่อเซ็ต</th>
              <th class="px-3 py-2 border-b border-gray-600">จำนวนชิ้นที่ต้องสวม</th>
              <th class="px-3 py-2 border-b border-gray-600">ไอเทมในเซ็ต</th>
              <th class="px-3 py-2 border-b border-gray-600">โบนัส</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="px-3 py-2 border-b border-gray-600">เซ็ตตำรวจ</td>
              <td class="px-3 py-2 border-b border-gray-600">4 / 7 ชิ้น</td>
              <td class="px-3 py-2 border-b border-gray-600">หมวกตำรวจ, เสื้อตำรวจ, เสื้อคลุมตำรวจ, กางเกงตำรวจ, รองเท้าหนัง, เข็มขัดตำรวจ, กระบองตำรวจ</td>
              <td class="px-3 py-2 border-b border-gray-600">
                <strong>4 ชิ้น:</strong> Critical +2%, Evasion +2%, ATK +10<br>
                <strong>7 ชิ้น:</strong> Critical +5%, Evasion +5%, ATK +30
              </td>
            </tr>
            <tr>
              <td class="px-3 py-2 border-b border-gray-600">เซ็ตทหาร</td>
              <td class="px-3 py-2 border-b border-gray-600">4 / 8 ชิ้น</td>
              <td class="px-3 py-2 border-b border-gray-600">หมวกทหาร, เสื้อกองทัพบก, เสื้อคลุมทหาร, ถุงมือทหาร, กางเกงทหาร, รองเท้าคอมแบท, เข็มขัดทหาร, แท่งเหล็กปลายมีด</td>
              <td class="px-3 py-2 border-b border-gray-600">
                <strong>4 ชิ้น:</strong> Critical +4%, Evasion +4%, ATK +20<br>
                <strong>8 ชิ้น:</strong> Critical +10%, Evasion +10%, ATK +50
              </td>
            </tr>
            <tr>
              <td class="px-3 py-2 border-b border-gray-600">เซ็ตนักดับเพลิง</td>
              <td class="px-3 py-2 border-b border-gray-600">4 / 8 ชิ้น</td>
              <td class="px-3 py-2 border-b border-gray-600">หมวกนิรภัย, เสื้อนักดับเพลิง, เสื้อคลุมนักดับเพลิง, ถุงมือนักดับเพลิง, กางเกงนักดับเพลิง, รองเท้านักดับเพลิง, เข็มขัดนักดับเพลิง, เลื่อยไฟฟ้า</td>
              <td class="px-3 py-2 border-b border-gray-600">
                <strong>4 ชิ้น:</strong> Max HP +1000, Critical +4%, Defense +20<br>
                <strong>8 ชิ้น:</strong> Max HP +2500, Critical +10%, Defense +50 atk +10
              </td>
            </tr>
            <tr>
              <td class="px-3 py-2 border-b border-gray-600">เซ็ตตัวตลก</td>
              <td class="px-3 py-2 border-b border-gray-600">2 / 5 ชิ้น</td>
              <td class="px-3 py-2 border-b border-gray-600">ถุงมือตัวตลก, เสื้อตัวตลก, กางเกงตัวตลก, หมวกตัวตลก, รองเท้าตัวตลก</td>
              <td class="px-3 py-2 border-b border-gray-600">
                <strong>2 ชิ้น:</strong> ATK +15, Critical +10%<br>
                <strong>5 ชิ้น:</strong> ATK +40, Critical +20%
              </td>
            </tr>
            <tr>
              <td class="px-3 py-2 border-b border-gray-600">เซ็ตเทควันโด้</td>
              <td class="px-3 py-2 border-b border-gray-600">3 ชิ้น</td>
              <td class="px-3 py-2 border-b border-gray-600">เสื้อเทควันโด, เสื้อคลุมเทควันโด, กางเกงเทควันโด</td>
              <td class="px-3 py-2 border-b border-gray-600">
                <strong>3 ชิ้น:</strong> Evasion +20%, ATK +30
              </td>
            </tr>
            <tr>
              <td class="px-3 py-2 border-b border-gray-600">เซ็ตนักรบไทย</td>
              <td class="px-3 py-2 border-b border-gray-600">6 ชิ้น</td>
              <td class="px-3 py-2 border-b border-gray-600">
                เกราะนักรบไทย, ปลอกแขนนักรบไทย, สนับแข้งนักรบไทย, หมวกนักรบไทย, หมวกอัศวิน, ดาบ
              </td>
              <td class="px-3 py-2 border-b border-gray-600">
                <strong>3 ชิ้น:</strong> Critical +3, Evasion +3, ATK +20<br>
                <strong>4 ชิ้น:</strong> Critical +6, Evasion +6, ATK +30<br>
                <strong>5 ชิ้น:</strong> Critical +10, Evasion +10, ATK +40
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div>
        <span class="text-indigo-400">หมายเหตุ:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>โบนัสจะทำงานอัตโนมัติเมื่อสวมใส่ไอเทมครบตามจำนวนที่กำหนด</li>
          <li>หากถอดไอเทมออกจนจำนวนชิ้นต่ำกว่าที่กำหนด โบนัสของระดับนั้นจะหายไปทันที</li>
        </ul>
      </div>
    </div>
  </article>
</section>
<!-- Upgrade System -->
<section class="space-y-6 mt-6">
  <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-arrow-up mr-2"></i> ระบบอัปเกรด
  </h2>

  <!-- Equipment Upgrade -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-tools mr-2"></i> การอัปเกรดอุปกรณ์
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">การอัปเกรดอุปกรณ์คืออะไร?</span> การอัปเกรดไอเทมเพื่อเพิ่มค่าพลัง เช่น atk, stamina, defense โดยใช้วัสดุเฉพาะและมีโอกาสสำเร็จตามระดับ</p>
      <div>
        <span class="text-indigo-400">ไอเทมที่อัปเกรดได้?</span>
        <ul class="list-disc pl-5 mt-1">
          <li>หมวก, แว่นตา, เสื้อ, เสื้อคลุม, เกราะ, ปลอกแขน, ถุงมือ, กางเกง, สนับแข้ง, รองเท้า, เข็มขัด, สร้อยคอ, แหวน, อาวุธ, อาวุธสำรอง, ตะกรุด, ผ้ายันต์, สายสิญจน์</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">ต้องใช้วัสดุอะไรบ้าง?</span>
        <ul class="list-disc pl-5 mt-1">
          <li><strong>เลเวล 1-5:</strong> ไอเทมชื่อเดียวกันกับไอเทมที่อัป 1 ชิ้น + เหล็กกล้า 3×(ระดับ+1) ชิ้น</li>
          <li><strong>เลเวล 6-8:</strong> ไอเทมชื่อเดียวกันกับไอเทมที่อัป 1 ชิ้น + แผ่นเหล็กกล้า 1 ชิ้น + แผ่นเคฟลาร์ตามระดับ (เลเวล 6: 1 ชิ้น, เลเวล 7: 2 ชิ้น, เลเวล 8: 3 ชิ้น)</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">โอกาสสำเร็จ?</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ระดับ 0 → 1: 60%</li>
          <li>ระดับ 1 → 2: 50%</li>
          <li>ระดับ 2 → 3: 40%</li>
          <li>ระดับ 3 → 4: 40%</li>
          <li>ระดับ 4 → 5: 40%</li>
          <li>ระดับ 5 → 6: 30%</li>
          <li>ระดับ 6 → 7: 20%</li>
          <li>ระดับ 7 → 8: 10%</li>
          <li>เพิ่มโอกาสจากวัสดุ: ไอเทมชื่อเดียวกันที่มีระดับ (เช่น ดาบ +1) เพิ่มโอกาส +5% ต่อระดับ สูงสุด +25% (ใช้ได้เฉพาะเลเวล 1-5)</li>
          <li>อัปเกรดได้สูงสุดระดับ 8 (ขึ้นกับ rarity: 7 ดาว +1, 8 ดาว +2, 9 ดาว +3, 10 ดาว +8)</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">เงื่อนไขวัสดุซ้ำชื่อ?</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ไอเทมวัสดุที่มีชื่อเดียวกันกับไอเทมที่กำลังอัปเกรด (เช่น ถุงมือทหาร อีก 1 ชิ้น สำหรับอัปเกรดถุงมือทหาร) ต้องมีสถานะ <strong>transfer เหมือนกับไอเทมที่กำลังอัปเกรด</strong></li>
          <li>ถ้าไอเทมที่อัปเกรดมี <strong>transfer=true</strong> วัสดุซ้ำชื่อก็ต้องเป็น <strong>transfer=true</strong></li>
          <li>ถ้าไอเทมที่อัปเกรดมี <strong>transfer=false</strong> วัสดุซ้ำชื่อก็ต้องเป็น <strong>transfer=false</strong></li>
          <li>วัสดูอื่น เช่น เหล็กกล้า แผ่นเหล็กกล้า แผ่นเคฟลาร์ ไม่ต้องสนใจสถานะ transfer</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">ระบบการันตีโอกาสสำเร็จ?</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ทุกครั้งที่อัปเกรดล้มเหลว ผู้เล่นจะได้รับ <strong>1 สิทธิ์การันตี</strong> สำหรับไอเทมชื่อนั้นและระดับนั้น (เช่น ดาบ +1 → +2 ล้มเหลว ได้ 1 สิทธิ์สำหรับ ดาบ +1 → +2 เท่านั้น)</li>
          <li>1 สิทธิ์การันตีเพิ่มโอกาสสำเร็จ +10%, สามารถใช้ได้สูงสุดจนโอกาสรวมถึง 80% (รวมโอกาสพื้นฐานและโบนัสจากวัสดุ)</li>
          <li>ผู้เล่นเลือกได้ว่าจะใช้กี่สิทธิ์ (0 ถึงจำนวนสิทธิ์ที่มี) แต่ไม่สามารถเลือกใช้สิทธิ์หากโอกาสรวมเกิน 80%</li>
          <li>เมื่ออัปเกรดสำเร็จ สิทธิ์ที่ใช้จะถูกลบ (เช่น ใช้ 2 สิทธิ์ สิทธิ์จะลดลง 2)</li>
          <li>หากไม่มีสิทธิ์การันตี (0 สิทธิ์) จะไม่สามารถเลือกใช้สิทธิ์ได้</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">ผลลัพธ์เมื่อสำเร็จ?</span>
        <p class="mt-1">ค่าพลังจะเพิ่มแบบสะสมตามตารางด้านล่าง</p>
        <div class="overflow-x-auto mt-2">
          <table class="min-w-full text-xs border border-gray-600">
            <thead>
              <tr class="bg-gray-800 text-indigo-300">
                <th class="border border-gray-600 px-2 py-1 text-left">กลุ่มไอเทม</th>
                <th class="border border-gray-600 px-2 py-1 text-left">+1</th>
                <th class="border border-gray-600 px-2 py-1 text-left">+2</th>
                <th class="border border-gray-600 px-2 py-1 text-left">+3</th>
                <th class="border border-gray-600 px-2 py-1 text-left">+4</th>
                <th class="border border-gray-600 px-2 py-1 text-left">+5</th>
                <th class="border border-gray-600 px-2 py-1 text-left">+6</th>
                <th class="border border-gray-600 px-2 py-1 text-left">+7</th>
                <th class="border border-gray-600 px-2 py-1 text-left">+8</th>
                <th class="border border-gray-600 px-2 py-1 text-left">สะสม +8</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="border border-gray-600 px-2 py-1">ATK Group<br><span class="text-gray-400">(แว่นตา, ปลอกแขน, ถุงมือ, สนับแข้ง, สร้อยคอ, แหวน, อาวุธ, ตะกรุด, ผ้ายันต์, สายสิญจน์)</span></td>
                <td class="border border-gray-600 px-2 py-1">atk +3</td>
                <td class="border border-gray-600 px-2 py-1">atk +6</td>
                <td class="border border-gray-600 px-2 py-1">atk +9</td>
                <td class="border border-gray-600 px-2 py-1">atk +12</td>
                <td class="border border-gray-600 px-2 py-1">atk +15</td>
                <td class="border border-gray-600 px-2 py-1">atk +18<br>agi +3</td>
                <td class="border border-gray-600 px-2 py-1">atk +21<br>agi +6</td>
                <td class="border border-gray-600 px-2 py-1">atk +24<br>agi +9</td>
                <td class="border border-gray-600 px-2 py-1 font-semibold text-green-400">atk +108<br>agi +18</td>
              </tr>
              <tr>
                <td class="border border-gray-600 px-2 py-1">DEF Group<br><span class="text-gray-400">(หมวก, เสื้อ, เสื้อคลุม, เกราะ, กางเกง, รองเท้า, เข็มขัด, อาวุธสำรอง)</span></td>
                <td class="border border-gray-600 px-2 py-1">sta +1<br>def +1</td>
                <td class="border border-gray-600 px-2 py-1">sta +2<br>def +2</td>
                <td class="border border-gray-600 px-2 py-1">sta +3<br>def +3</td>
                <td class="border border-gray-600 px-2 py-1">sta +4<br>def +4</td>
                <td class="border border-gray-600 px-2 py-1">sta +5<br>def +5</td>
                <td class="border border-gray-600 px-2 py-1">sta +6<br>def +6<br>agi +3</td>
                <td class="border border-gray-600 px-2 py-1">sta +7<br>def +7<br>agi +6</td>
                <td class="border border-gray-600 px-2 py-1">sta +8<br>def +8<br>agi +9</td>
                <td class="border border-gray-600 px-2 py-1 font-semibold text-green-400">sta +36<br>def +36<br>agi +18</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <span class="text-indigo-400">ผลลัพธ์เมื่อล้มเหลว?</span>
        <ul class="list-disc pl-5 mt-1">
          <li>สูญเสียวัสดุทั้งหมด แต่ไอเทมหลักยังคงอยู่</li>
          <li>ได้รับ 1 สิทธิ์การันตีสำหรับไอเทมชื่อนั้นและระดับนั้น</li>
        </ul>
      </div>
    </div>
  </article>
</section>  <!-- Disarm Trap System -->
<section class="space-y-6 mt-6">
  <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-unlock mr-2"></i> ระบบปลดกัปดัก
  </h2>

  <!-- Disarm Trap Usage -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-tools mr-2"></i> การปลดกัปดักคืออะไร?
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p>
        <span class="text-indigo-400">การปลดกัปดัก</span>
        คือการใช้ไอเทมประเภท <strong>"ปลดกัปดัก"</strong> เพื่อตรวจสอบและทำลายกัปดักที่ติดตั้งไว้โดยผู้รอดชีวิตคนอื่น
        หากปลดสำเร็จกับดักนั้นจะหายไปทันทีและไม่สามารถทำงานได้อีก
      </p>

      <div>
        <span class="text-indigo-400">ขั้นตอนการใช้งาน</span>
        <ul class="list-disc pl-5 mt-1">
          <li>เลือกเป้าหมายผู้รอดชีวิตที่จะตรวจสอบ (ต้องค้นหาเป้าหมายก่อนใช้งาน)</li>
          <li>เลือกไอเทม "ปลดกัปดัก" ในกระเป๋า</li>
          <li>เลือกจำนวนห้องตามที่ไอเทมกำหนด (เช่น ต้องเลือก 2 ห้องจากทั้งหมด)</li>
          <li>กด <strong>ยืนยันปลดกัปดัก</strong> เพื่อเริ่มดำเนินการ</li>
        </ul>
      </div>

      <div>
        <span class="text-indigo-400">ทรัพยากรที่ต้องใช้</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ไอเทม "ปลดกัปดัก" 1 ชิ้น (ไอเทมจะถูกลบเสมอ ไม่ว่าผลจะสำเร็จหรือไม่)</li>
        </ul>
      </div>

      <div>
        <span class="text-indigo-400">ผลลัพธ์ที่เป็นไปได้</span>
        <ul class="list-disc pl-5 mt-1">
          <li><strong class="text-green-400">สำเร็จ:</strong> พบและปลดกัปดักได้สำเร็จ กัปดักจะหายไป ผู้ติดตั้งจะได้รับการแจ้งเตือนว่ากับดักถูกปลด</li>
          <li><strong class="text-red-400">ล้มเหลว:</strong> ถ้าไม่พบกัปดักในห้องที่เลือก ระบบจะแจ้งว่า "ไม่พบกัปดัก" และไอเทมจะถูกลบ</li>
        </ul>
      </div>

      <div>
        <span class="text-indigo-400">การแจ้งเตือน</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ฝั่งผู้ปลดกัปดัก: จะได้รับแจ้งผลสำเร็จหรือล้มเหลว</li>
          <li>ฝั่งเจ้าของกัปดัก: จะได้รับแจ้งเตือนหากกัปดักถูกปลด หรือมีคนพยายามปลด</li>
        </ul>
      </div>

      <div>
        <span class="text-indigo-400">ข้อควรระวัง</span>
        <ul class="list-disc pl-5 mt-1">
          <li>การปลดกัปดักจะลบไอเทมทันที ไม่สามารถกู้คืนได้</li>
          <li>ต้องเลือกจำนวนห้องให้ครบตามที่ไอเทมกำหนด ไม่เช่นนั้นจะไม่สามารถยืนยันได้</li>
          <li>เป้าหมายต้องมีกับดักที่ยังไม่หมดอายุและยังไม่ได้ถูกยกเลิก</li>
        </ul>
      </div>
    </div>
  </article>
</section>

        <!-- Bomb Item Usage System -->
    <section class="space-y-6 mt-6">
      <h2 class="text-lg font-semibold text-indigo-200 mb-4">
        <i class="fas fa-bomb mr-2"></i> วิธีการใช้งานไอเทมประเภทระเบิด
      </h2>

      <!-- Bomb Item Usage -->
      <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
          <i class="fas fa-bomb mr-2"></i> การใช้งานไอเทมระเบิด
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
          <p><span class="text-indigo-400">ไอเทมระเบิดคืออะไร?</span> ไอเทมประเภท 'ระเบิด' ที่ใช้ติดตั้งในห้องของผู้รอดชีวิตเพื่อทำความเสียหาย หากเดาห้องถูก ผู้รอดชีวิตจะได้รับดาเมจตามค่าของระเบิด</p>
          <div>
            <span class="text-indigo-400">ใช้งานยังไง?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>เลือกไอเทมระเบิดในกระเป๋า แล้วกด "ติดตั้งระเบิด" เพื่อเปิดหน้าต่างเลือกห้อง</li>
              <li>เลือกห้องที่คิดว่าผู้รอดชีวิตซ่อนอยู่</li>
              <li>ระบบจะย้ายห้องของเป้าหมายอัตโนมัติทุกครั้ง ไม่ว่าจะเลือกถูกหรือไม่</li>
            </ul>
          </div>
          <div>
            <span class="text-indigo-400">ต้องใช้ทรัพยากรอะไรบ้าง?</span>
            <ul class="list-disc pl-5 mt-1">
              <li>ไอเทมระเบิด 1 ชิ้น </li>
              <li>แต่จำกัดจำนวนครั้ง 3 ครั้งต่อเป้าหมาย, 6 ครั้งต่อวัน เหมือนลอบสังหาร</li>
            </ul>
          </div>
          <div>
            <span class="text-indigo-400">ผลลัพธ์ที่เป็นไปได้?</span>
            <ul class="list-disc pl-5 mt-1">
              <li><strong>เดาห้องถูก:</strong> ทำดาเมจ = ค่าระเบิด - defense ของเป้าหมาย อาจทำลายกัปดัก (ถ้ามี) และอาจฆ่าเป้าหมายได้ หากตายจะได้รับเพชรและทอง 50% หักภาษี 30%</li>
              <li><strong>เดาห้องผิด:</strong> ระเบิดทำงานแต่ไม่โดนใคร ไอเทมสูญเสียแต่ไม่ได้รับรางวัล</li>
            </ul>
          </div>
        </div>
      </article>
    </section>
    <section class="space-y-6 mt-6">
  <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-shopping-cart mr-2"></i> ระบบพ่อค้า
  </h2>

  <!-- Finding Merchants -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-search mr-2"></i> การค้นหาพ่อค้า
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">การค้นหาพ่อค้าคืออะไร?</span> คุณสามารถค้นหาพ่อค้าได้ตามสถานที่ต่างๆ ในแผนที่ เพื่อซื้อไอเทมหรือปล้นทรัพยากร</p>
      <div>
        <span class="text-indigo-400">ค้นหาได้ยังไง?</span>
        <ul class="list-disc pl-5 mt-1">
          <li>เลือกแผนที่และสำรวจเพื่อมีโอกาสพบพ่อค้า</li>
          <li>เมื่อพบ จะเริ่มอีเวนต์กับพ่อค้าเพื่อซื้อหรือปล้น</li>
        </ul>
      </div>
      <p><span class="text-indigo-400">เคล็ดลับ:</span> สำรวจแผนที่บ่อยๆ เพื่อเพิ่มโอกาสพบพ่อค้าที่มีไอเทมดีๆ!</p>
    </div>
  </article>

  <!-- Robbing Merchants -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-skull-crossbones mr-2"></i> การปล้นพ่อค้า
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">การปล้นพ่อค้าคืออะไร?</span> การโจมตีพ่อค้าเพื่อขโมยไอเทมและทรัพยากร โดยไม่ใช้พลังงานในการโจมตี</p>
      <div>
        <span class="text-indigo-400">ขั้นตอนการปล้น?</span>
        <ul class="list-disc pl-5 mt-1">
          <li>เมื่อพบพ่อค้า เลือกตัวเลือกปล้นเพื่อเริ่มต่อสู้</li>
          <li>ระหว่างสู้ จะไม่แสดง HP ของพ่อค้า</li>
          <li>HP พ่อค้าจะสุ่มทุกครั้ง (เช่น สูงสุด 1000 จะสุ่มระหว่าง 100-1000)</li>
          <li>โจมตีพ่อค้าจนตายเพื่อรับรางวัล หรือหากคุณตายจะถูกลงโทษ</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">ผลลัพธ์ที่เป็นไปได้?</span>
        <ul class="list-disc pl-5 mt-1">
          <li><strong>พ่อค้าตาย:</strong> ได้รับไอเทมทั้งหมดจากพ่อค้า, ทรัพยากร (เพชร, ทอง, เหรียญเงิน) และแสดง HP เหลือใน Log</li>
          <li><strong>คุณตาย:</strong> สร้างอีเวนต์เกิดใหม่ เสียไอเทมสุ่ม และแสดง HP เหลือของพ่อค้าใน Log</li>
        </ul>
      </div>
      <p><span class="text-indigo-400">เคล็ดลับ:</span> เตรียมตัวละครให้แข็งแกร่งก่อนปล้น เพราะไม่รู้ HP จริงของพ่อค้า!</p>
    </div>
  </article>

  <!-- Buying from Merchants -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-coins mr-2"></i> การซื้อไอเทมจากพ่อค้า
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">การซื้อไอเทมคืออะไร?</span> ใช้ทรัพยากร (เพชร, ทอง, เหรียญเงิน) ซื้อไอเทมจากพ่อค้าที่พบ</p>
      <div>
        <span class="text-indigo-400">ต้องใช้ทรัพยากรอะไรบ้าง?</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ทรัพยากรตามราคาไอเทม (เช่น เหรียญเงิน 100)</li>
          <li>กระเป๋าต้องมีน้ำหนักเหลือพอสำหรับไอเทม</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">ขั้นตอนการซื้อ?</span>
        <ul class="list-disc pl-5 mt-1">
          <li>เลือกไอเทมจากรายการของพ่อค้า</li>
          <li>ยืนยันการซื้อ หักทรัพยากรและเพิ่มไอเทมเข้าคลัง</li>
          <li>ไอเทมจะถูกลบจากพ่อค้า ไม่สามารถซื้อซ้ำได้</li>
        </ul>
      </div>
      <p><span class="text-indigo-400">ข้อจำกัด?</span> ต้องพบพ่อค้าก่อน, มีทรัพยากรพอ, และกระเป๋าไม่เต็ม</p>
      <p><span class="text-indigo-400">เคล็ดลับ:</span> ซื้อไอเทมดีๆ ก่อนปล้น หากคิดว่าสามารถฆ่าพ่อค้าได้!</p>
    </div>
  </article>
</section>
  <section class="space-y-6 mt-6">
    <h2 class="text-lg font-semibold text-indigo-200 mb-4">
      <i class="fas fa-flag mr-2"></i> ระบบยึดครองแผนที่
    </h2>

    <!-- Map Occupation Overview -->
    <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
      <h3 class="text-base font-semibold text-indigo-200 mb-2">
        <i class="fas fa-info-circle mr-2"></i> ภาพรวมระบบยึดครองแผนที่
      </h3>
      <div class="text-indigo-200 text-sm space-y-2">
        <p><span class="text-indigo-400">ระบบยึดครองแผนที่คืออะไร?</span> ระบบที่ให้ผู้เล่นสามารถท้าประลองเพื่อยึดครองแผนที่ หรือเคลียร์ผู้ยึดครองเดิมเพื่อให้ NPC เข้ามาครองแทน ผู้ยึดครองจะได้รับรางวัลพิเศษ เช่น เพชรรายวันและโอกาสเปิดกล่องสมบัติทุกวันเวลา 21:00 น.</p>
        <p><span class="text-indigo-400">เจอผู้ยึดครองได้ยังไง?</span> เมื่อสำรวจแผนที่ มีโอกาส 20% ที่จะพบผู้ยึดครอง (ผู้เล่นอื่นหรือ NPC) ซึ่งสามารถเลือกท้าประลองหรือเคลียร์ได้</p>
      </div>
    </article>
    <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
        <h3 class="text-base font-semibold text-indigo-200 mb-2">
            <i class="fas fa-user-secret mr-2"></i> ค่าพลังของ NPC ผู้ยึดครอง
        </h3>
        <div class="text-indigo-200 text-sm space-y-2">
            <p><span class="text-indigo-400">ภาพรวม:</span> เมื่อพบผู้ยึดครองที่เป็น NPC ในระบบยึดครองแผนที่ NPC จะมีค่าพลังคงที่ ดังนี้:</p>
            <ul class="list-disc pl-5 mt-1">
                <li><strong>พลังชีวิตสูงสุด (Max HP):</strong> 6,000 </li>
                <li><strong>พลังโจมตี (Attack):</strong> 200 </li>
                <li><strong>การป้องกัน (Defense):</strong> 50</li>
                <li><strong>โอกาสคริติคอล (Critical Probability):</strong> 50% </li>
                <li><strong>โอกาสหลบหลีก (Evasion Probability):</strong> 50% </li>
                <li><strong>การลดความเสียหาย (Damage Reduction):</strong> 2.5% </li>
            </ul>
            <p><span class="text-indigo-400">เคล็ดลับ:</span> เตรียมตัวละครให้มีพลังโจมตีสูงกว่า 50, การป้องกันสูง, และพลังชีวิตมากพอ (แนะนำมากกว่า 6,000) เพื่อรับมือกับ NPC ผู้ยึดครอง!</p>
        </div>
    </article>
    <!-- Challenging an Occupier -->
    <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
      <h3 class="text-base font-semibold text-indigo-200 mb-2">
        <i class="fas fa-fist-raised mr-2"></i> การท้าประลองผู้ยึดครอง
      </h3>
      <div class="text-indigo-200 text-sm space-y-2">
        <p><span class="text-indigo-400">การท้าประลองคืออะไร?</span> การต่อสู้กับผู้ยึดครองปัจจุบัน (ผู้เล่นหรือ NPC) เพื่อแย่งชิงตำแหน่งผู้ยึดครองแผนที่</p>
        <div>
          <span class="text-indigo-400">เงื่อนไขการท้าประลอง:</span>
          <ul class="list-disc pl-5 mt-1">
            <li>ต้องไม่มีแผนที่ที่ยึดครองอยู่แล้ว</li>
            <li>ต้องใช้เพชรตามที่แผนที่กำหนด</li>
          </ul>
        </div>
        <div>
          <span class="text-indigo-400">ขั้นตอนการท้าประลอง:</span>
          <ul class="list-disc pl-5 mt-1">
            <li>เลือก "ท้าประลอง" เมื่อพบผู้ยึดครอง</li>
            <li>ใช้เพชรตามที่กำหนด และเริ่มการต่อสู้ (มีเวลา 15 นาที)</li>
            <li>ผู้ยึดครองจะตอบโต้ทุกครั้งที่ถูกโจมตี</li>
            <li>ผู้ท้าประลองสามารถหนีได้ 100%</li>
          </ul>
        </div>
        <div>
          <span class="text-indigo-400">ผลลัพธ์ที่เป็นไปได้:</span>
          <ul class="list-disc pl-5 mt-1">
            <li><strong class="text-green-400">ชนะ:</strong> คุณกลายเป็นผู้ยึดครองแผนที่ และได้รับรางวัล เช่น เพชร, ไอเทม และสิทธิ์เปิดกล่องสมบัติรายวัน</li>
            <li><strong class="text-red-400">แพ้:</strong> เลือดลดเหลือ 1 HP, อีเวนต์จบลง, ไม่เสียไอเทมหรือทรัพยากร</li>
          </ul>
        </div>
      </div>
    </article>

    <!-- Clearing an Occupier -->
    <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
      <h3 class="text-base font-semibold text-indigo-200 mb-2">
        <i class="fas fa-broom mr-2"></i> การเคลียร์ผู้ยึดครอง
      </h3>
      <div class="text-indigo-200 text-sm space-y-2">
        <p><span class="text-indigo-400">การเคลียร์คืออะไร?</span> การต่อสู้เพื่อถอนผู้ยึดครองปัจจุบัน (ผู้เล่น) ออกจากแผนที่ และให้ NPC เข้ามาครองแทน</p>
        <div>
          <span class="text-indigo-400">เงื่อนไขการเคลียร์:</span>
          <ul class="list-disc pl-5 mt-1">
            <li>ต้องอยู่ในอีเวนต์สำรวจและพบผู้ยึดครอง (ที่ไม่ใช่ NPC)</li>
            <li>ผู้ท้าประลองสามารถหนีได้ 100%</li>
          </ul>
        </div>
        <div>
          <span class="text-indigo-400">ขั้นตอนการเคลียร์:</span>
          <ul class="list-disc pl-5 mt-1">
            <li>เลือก "เคลียร์ผู้ยึดครอง" เมื่อพบผู้ยึดครอง</li>
          </ul>
        </div>
        <div>
          <span class="text-indigo-400">ผลลัพธ์ที่เป็นไปได้:</span>
          <ul class="list-disc pl-5 mt-1">
            <li><strong class="text-green-400">สำเร็จ:</strong> ผู้ยึดครองเดิมถูกถอนออก, NPC เข้ามาครองแผนที่, คุณไม่ได้รับตำแหน่งผู้ยึดครอง</li>
            <li><strong class="text-red-400">แพ้:</strong> เลือดลดเหลือ 1 HP, อีเวนต์จบลง, ไม่เสียไอเทมหรือทรัพยากร</li>
          </ul>
        </div>
        <p><span class="text-indigo-400">เคล็ดลับ:</span> ใช้การเคลียร์เมื่อต้องการกำจัดผู้ยึดครองที่แข็งแกร่ง โดยไม่ต้องรับตำแหน่งเพื่อหลีกเลี่ยงการถูกท้าประลองในอนาคต!</p>
      </div>
    </article>


    <!-- Opening Treasure Box -->
    <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
      <h3 class="text-base font-semibold text-indigo-200 mb-2">
        <i class="fas fa-treasure-chest mr-2"></i> การเปิดกล่องสมบัติแผนที่
      </h3>
      <div class="text-indigo-200 text-sm space-y-2">
        <p><span class="text-indigo-400">การเปิดกล่องสมบัติคืออะไร?</span> ผู้ยึดครองแผนที่สามารถเปิดกล่องสมบัติทุกวันเวลา 21:00 น. เพื่อรับรางวัล เช่น รางวัลเพชรรายวัน(แต่ละแผนที่จะได้รับไม่เท่ากัน) และ ไอเทมที่อยู่ในแผนที่นั้น</p>
        <div>
          <span class="text-indigo-400">เงื่อนไขการเปิดกล่อง:</span>
          <ul class="list-disc pl-5 mt-1">
            <li>ต้องเป็นผู้ยึดครองแผนที่</li>
            <li>กล่องสมบัติต้องยังไม่ถูกเปิดในวันนั้น (รีเซ็ตทุกวันเวลา 21:00 น.)</li>
          </ul>
        </div>
      </div>
    </article>

    <!-- Resigning as Occupier -->
    <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
      <h3 class="text-base font-semibold text-indigo-200 mb-2">
        <i class="fas fa-sign-out-alt mr-2"></i> การถอนตัวจากการยึดครอง
      </h3>
      <div class="text-indigo-200 text-sm space-y-2">
        <p><span class="text-indigo-400">การถอนตัวคืออะไร?</span> การสละตำแหน่งผู้ยึดครองแผนที่ ทำให้แผนที่ว่างลงและเปิดโอกาสให้ผู้อื่นยึดครอง</p>
        <div>
          <span class="text-indigo-400">เงื่อนไขการถอนตัว:</span>
          <ul class="list-disc pl-5 mt-1">
            <li>ต้องเป็นผู้ยึดครองแผนที่</li>
            <li>ตัวละครต้องมีชีวิตอยู่</li>
          </ul>
        </div>
        <div>
          <span class="text-indigo-400">ขั้นตอนการถอนตัว:</span>
          <ul class="list-disc pl-5 mt-1">
            <li>เลือก "ถอนตัวจากการยึดครอง" ในเมนูสำรวจหรือคลิกไปที่แผนที่</li>
            <li>ยืนยันการถอนตัว แผนที่จะว่างลงทันที และ npc จะเข้ามาแทน</li>
          </ul>
        </div>
      </div>
    </article>
  </section>
  <section class="space-y-6 mt-6">
  <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-sack-dollar mr-2"></i> ระบบการใช้งานไอเทมประเภทปล้น
  </h2>
  <!-- Loot Item Overview -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-info-circle mr-2"></i> ภาพรวมระบบการปล้น
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ระบบการปล้นคืออะไร?</span> ระบบที่ให้ผู้เล่นใช้ไอเทมประเภท "ปล้น" เพื่อดักรอผู้เล่นอื่นในแผนที่ที่กำหนด เพื่อต่อสู้และชิงทรัพยากรหรือไอเทมจากผู้ถูกดัก ไอเทมจะถูกลบออกจากกระเป๋าทันทีเมื่อเริ่มการดักรอ</p>
      <p><span class="text-indigo-400">การเริ่มต้น:</span> ผู้เล่นเลือกแผนที่ที่อนุญาตให้ปล้นและใช้ไอเทมปล้นเพื่อเริ่มการดักรอ</p>
      <p><span class="text-indigo-400">ผลลัพธ์:</span> หากพบผู้เล่นอื่น จะเข้าสู่การต่อสู้ ผู้ชนะจะได้รับรางวัล เช่น เพชร, ทอง, เงิน หรือไอเทมจากกระเป๋าของผู้แพ้</p>
    </div>
  </article>
  <!-- Using Loot Item -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-sack-dollar mr-2"></i> การใช้ไอเทมปล้น
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ขั้นตอนการใช้:</span></p>
      <ul class="list-disc pl-5 mt-1">
        <li>เลือกไอเทมปล้นในกระเป๋า เช่น "หมวกไอ้โม่ง"</li>
        <li>เลือกแผนที่ที่อนุญาตให้ปล้น (ต้องมีเลเวลตัวละครถึงตามที่แผนที่กำหนด)</li>
        <li>กด "เริ่มดักรอปล้น" ไอเทมจะถูกลบออกจากกระเป๋าทันที และระบบจะเริ่มนับเวลาดักรอ</li>
        <li>ผู้เล่นจะได้รับ Event ดักรอปล้น ซึ่งต้องรอผู้เล่นอีกฝ่ายเข้าในแผนที่ที่ผู้เล่นเลือก จากนั้นกดกลับบ้าน จะเจอผู้เล่นดักรอปล้นอยู่</li>
      </ul>
      <p><span class="text-indigo-400">หมายเหตุ:</span> หากไม่พบผู้เล่นภายในเวลาที่กำหนด event จะถูกยกเลิกทันทีแบบอัตโนมัติ</p>
    </div>
  </article>
  <!-- Combat During Ambush -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-fist-raised mr-2"></i> การต่อสู้เมื่อพบผู้เล่น
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">เมื่อพบผู้เล่น:</span> ระบบจะเริ่มโหมดการต่อสู้</p>
      <div>
        <span class="text-indigo-400">การประมวลผลการต่อสู้:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ทั้งสองฝ่ายเลือกแอคชัน โจมตีหรือหนี และรอ 5 วินาทีต่อรอบ</li>
          <li>ผลลัพธ์ขึ้นอยู่กับ:
            <ul class="list-disc pl-5 mt-1">
              <li><strong>ทั้งคู่โจมตี:</strong> คำนวณความเสียหายทั้งสองฝ่าย</li>
              <li><strong>ฝ่ายใดฝ่ายหนึ่งหนี:</strong> มีโอกาสหนีสำเร็จตามค่าสถิติ หากสำเร็จ อีเวนต์จบลง</li>
              <li><strong>ทั้งคู่หนี:</strong> อีเวนต์จบลงทันที โดยไม่มีรางวัล</li>
            </ul>
          </li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">ผลลัพธ์ที่เป็นไปได้:</span>
        <ul class="list-disc pl-5 mt-1">
          <li><strong class="text-green-400">ชนะ:</strong> ได้รับรางวัล เช่น ไอเทม, เพชร, ทอง, เงิน หรือไอเทมจากกระเป๋าของผู้แพ้ ตามข้อมูลของไอเทมปล้น</li>
          <li><strong class="text-red-400">แพ้:</strong> พลังชีวิตลดเหลือ 1 HP และสูญเสียทรัพยากรตามที่กำหนด และไอเทมในกระเป๋าตามที่กำหนด</li>
          <li><strong class="text-yellow-400">หนีสำเร็จ:</strong> ออกจากอีเวนต์โดยไม่ได้รับหรือสูญเสียอะไร</li>
        </ul>
      </div>
    </div>
  </article>
  <!-- Collecting Rewards -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-bag mr-2"></i> การเก็บไอเทมใส่กระเป๋า
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">เมื่อผู้เล่นเป็นผู้ชนะ:</span> ผู้เล่นสามารถเก็บรางวัลจากผู้แพ้</p>
      <div>
        <span class="text-indigo-400">การเลือกไอเทมหรือทรัพยากร:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>เลือกไอเทมหรือทรัพยากร เพชร, ทอง, เงิน จากรางวัลที่ได้รับ</li>
          <li><strong class="text-yellow-400">คำเตือน:</strong> มีโอกาสที่ไอเทมจะกลายเป็น "กองขยะ" (น้ำหนัก 5) เมื่อนำใส่กระเป๋า</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">หมายเหตุ:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ไอเทมมีโอกาสกลายเป็นกองขยะได้ เพื่อป้องกันการโอนของระหว่างผู้เล่นโดยใช้วิธีการปล้น</li>
        </ul>
      </div>
    </div>
  </article>
</section>
<section class="space-y-6 mt-6">
  <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-shop mr-2"></i> ระบบตลาด
  </h2>

  <!-- Marketplace Overview -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-info-circle mr-2"></i> ภาพรวมระบบตลาด
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ระบบตลาดคืออะไร?</span> ระบบตลาดเป็นสถานที่ที่ผู้เล่นสามารถตั้งร้านค้าเพื่อขายไอเทมที่สามารถโอนได้จากกระเป๋าของตนเอง และซื้อไอเทมจากร้านค้าของผู้เล่นอื่น โดยใช้ <strong>เพชร</strong> เป็นสกุลเงินหลักในการซื้อขาย</p>
      <ul class="list-disc pl-5 mt-1">
        <li>ตั้งร้านค้าได้ 1 ครั้ง / 2 วัน (ครั้งละ 9 ชั่วโมง) (รีเซ็ตทุกวันเวลา 21:00 น.)</li>
        <li>เลือกได้ว่าจะปิดบังตัวตนหรือแสดงชื่อตัวละคร</li>
        <li>กำหนดราคาไอเทมได้เอง ขั้นต่ำ 10 เพชร และรับเพชรสุทธิหลังหักภาษี 35%</li>
        <li>ซื้อไอเทมจากร้านค้าอื่นได้ทันทีหากมีเพชรและน้ำหนักกระเป๋าเพียงพอ</li>
      </ul>
    </div>
  </article>

</section>
<section class="space-y-6 mt-6">
  <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-ghost mr-2"></i> ระบบไอเทมวิญญาณเฝ้าห้อง
  </h2>
  <!-- Overview -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-info-circle mr-2"></i> ภาพรวมระบบวิญญาณเฝ้าห้อง
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">วิญญาณเฝ้าห้องคืออะไร?</span> วิญญาณเฝ้าห้องคือไอเทมประเภท 'วิญญาณ' ที่ผู้เล่นสามารถอัญเชิญเพื่อรับโบนัส stats และใช้งานเวทย์มนตร์พิเศษ โดยวิญญาณจะเฝ้าห้องแทนผู้เล่น หากผู้ลอบสังหารเดาห้องผิดจะต้องต่อสู้กับวิญญาณแทน (สุ่มจำนวนวิญญาณตามที่กำหนด)</p>
    </div>
  </article>
  <!-- Usage -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-magic mr-2"></i> การใช้งานเวทย์มนตร์วิญญาณเฝ้าห้อง
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ใช้งานเวทย์มนตร์ยังไง?</span> หลังจากอัญเชิญวิญญาณแล้ว กด "ใช้เวทย์มนตร์" ในหน้าต่างวิญญาณเพื่อสร้าง event วิญญาณเฝ้าห้อง</p>
    </div>
  </article>
  <!-- Effect on Assassination -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-user-secret mr-2"></i> ผลกระทบต่อการลอบสังหาร
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">เกิดอะไรขึ้นเมื่อเดาห้องผิด?</span> หากผู้ลอบสังหารเดาห้องผิดในขณะที่มีวิญญาณเฝ้าห้อง ระบบจะสร้าง event โจมตีวิญญาณเฝ้าห้อง ให้กับผู้ลอบสังหารที่เลือกห้องผิด</p>
      <div>
        <span>เมื่อมีผู้เล่นใช้งานไอเทมไล่ผี วิญญาณที่ถูกเรียกมาเฝ้าจะหายไปทันที</span>
        <span class="text-indigo-400">การต่อสู้กับวิญญาณ:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>วิญญาณจะสุ่มจากแผนที่โลกวิญญาณ</li>
          <li>ผู้ลอบสังหารต้องโจมตีวิญญาณทีละตัว จนกว่าจะกำจัดหมด</li>
          <li>วิญญาณจะโจมตีกลับหากยังไม่ตาย</li>
        </ul>
      </div>
    </div>
  </article>
  <!-- Punishment -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-hourglass-end mr-2"></i> การลงโทษเมื่อกำจัดวิญญาณไม่ทันเวลา
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">การลงโทษคืออะไร?</span> หากผู้ลอบสังหารไม่สามารถกำจัดวิญญาณทั้งหมดใน event ก่อนหมดเวลา ตัวละครจะถูกลงโทษโดยตายและสูญเสียทรัพยากรให้เจ้าของวิญญาณเฝ้าห้อง</p>
      <p><span class="text-indigo-400">เคล็ดลับ:</span> กำจัดวิญญาณให้ทันเวลาเพื่อหลีกเลี่ยงการลงโทษ!</p>
    </div>
  </article>
</section>
<section class="space-y-6 mt-6">
  <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-search mr-2"></i> ระบบไอเทมวิญญาณค้นหาผู้เล่น
  </h2>
  <!-- Overview -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-info-circle mr-2"></i> ภาพรวมระบบวิญญาณค้นหาผู้เล่น
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">วิญญาณค้นหาผู้เล่นคืออะไร?</span> วิญญาณค้นหาผู้เล่นคือไอเทมประเภท 'วิญญาณ' ที่ผู้เล่นสามารถอัญเชิญเพื่อรับโบนัส stats และใช้งานเวทย์มนตร์พิเศษ โดยวิญญาณจะช่วยคัดห้องที่เป้าหมาย (ผู้รอดชีวิต) ไม่อยู่ออกไป เพื่อลดจำนวนห้องที่เป็นไปได้ ทำให้เดาห้องจริงง่ายขึ้น</p>
      <p><span class="text-indigo-400">อัญเชิญวิญญาณยังไง?</span> เลือกไอเทมวิญญาณค้นหาผู้เล่นในกระเป๋า แล้วกด "อัญเชิญวิญญาณ" เพื่อติดตั้ง (แทนที่วิญญาณค้นหาผู้เล่นเดิมถ้ามี) และได้รับโบนัส stats เช่น stamina, int, evasion</p>
    </div>
  </article>
  <!-- Usage -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-magic mr-2"></i> การใช้งานเวทย์มนตร์วิญญาณค้นหาผู้เล่น
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ใช้งานเวทย์มนตร์ยังไง?</span> หลังจากอัญเชิญวิญญาณแล้ว กด "ใช้เวทย์มนตร์" ในหน้าต่างวิญญาณเพื่อค้นหาและคัดห้อง</p>
      <div>
        <span class="text-indigo-400">รายละเอียดเวทย์มนตร์:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ระบบคำนวณจำนวนห้องที่คัดออก ตามข้อมูลของไอเทม</li>
          <li>และจะย้ายต่ำแหน่งห้องใหม่ของผู้เล่นตรงข้ามด้วย แต่จะไม่อยู่ในห้องที่ถูกคัดออกแล้ว</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">ข้อจำกัด:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ใช้งานได้ 1 ครั้ง ต้องลอบสังหารหรือค้นหาใหม่เพื่อใช้งานได้อีกรอบ</li>
          
        </ul>
      </div>
      <p><span class="text-indigo-400">เคล็ดลับ:</span> ใช้เวทย์มนตร์ก่อนลอบสังหารเพื่อเพิ่มโอกาสเดาถูกห้อง โดยเฉพาะเมื่อเป้าหมายมีห้องจำนวนมาก!</p>
    </div>
  </article>
 <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-user-secret mr-2"></i> ผลกระทบต่อการลอบสังหาร
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">เกิดอะไรขึ้นหลังใช้งาน?</span> ห้องที่ถูกคัดออก จะถูกทำให้เห็น ในหน้าลอบสังหาร ทำให้เลือกห้องได้น้อยลงและเดาถูกง่ายขึ้น (เช่น จาก 10 ห้องเหลือ 5 ห้อง โอกาสถูก 20%)</p>
      <div>
        <span class="text-indigo-400">ตัวอย่างการคัดห้อง (สมมติวิญญาณคัดออกได้สูงสุด 5 ห้อง):</span>
        <div class="overflow-x-auto mt-2">
          <table class="min-w-full text-left text-xs border border-gray-700">
            <thead class="bg-gray-800">
              <tr>
                <th class="px-2 py-1 border-b border-gray-700">จำนวนห้องของเป้าหมาย (rooms)</th>
                <th class="px-2 py-1 border-b border-gray-700">ห้องขั้นต่ำที่เหลือ (minRemaining)</th>
                <th class="px-2 py-1 border-b border-gray-700">จำนวนห้องที่คัดออกสูงสุด (maxElim)</th>
                <th class="px-2 py-1 border-b border-gray-700">จำนวนห้องที่คัดออกจริง (elimCount)</th>
                <th class="px-2 py-1 border-b border-gray-700">ห้องที่เหลือหลังคัดออก</th>
                <th class="px-2 py-1 border-b border-gray-700">โอกาสเดาถูกหลังคัดออก (%)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="px-2 py-1 border-b border-gray-800">7</td>
                <td class="px-2 py-1 border-b border-gray-800">5</td>
                <td class="px-2 py-1 border-b border-gray-800">2</td>
                <td class="px-2 py-1 border-b border-gray-800">2</td>
                <td class="px-2 py-1 border-b border-gray-800">5</td>
                <td class="px-2 py-1 border-b border-gray-800">~20%</td>
              </tr>
              <tr>
                <td class="px-2 py-1 border-b border-gray-800">8</td>
                <td class="px-2 py-1 border-b border-gray-800">5</td>
                <td class="px-2 py-1 border-b border-gray-800">3</td>
                <td class="px-2 py-1 border-b border-gray-800">3</td>
                <td class="px-2 py-1 border-b border-gray-800">5</td>
                <td class="px-2 py-1 border-b border-gray-800">~20%</td>
              </tr>
              <tr>
                <td class="px-2 py-1 border-b border-gray-800">9</td>
                <td class="px-2 py-1 border-b border-gray-800">5</td>
                <td class="px-2 py-1 border-b border-gray-800">4</td>
                <td class="px-2 py-1 border-b border-gray-800">4</td>
                <td class="px-2 py-1 border-b border-gray-800">5</td>
                <td class="px-2 py-1 border-b border-gray-800">~20%</td>
              </tr>
              <tr>
                <td class="px-2 py-1 border-b border-gray-800">10</td>
                <td class="px-2 py-1 border-b border-gray-800">5</td>
                <td class="px-2 py-1 border-b border-gray-800">5</td>
                <td class="px-2 py-1 border-b border-gray-800">5</td>
                <td class="px-2 py-1 border-b border-gray-800">5</td>
                <td class="px-2 py-1 border-b border-gray-800">~20%</td>
              </tr>
              <tr>
                <td class="px-2 py-1 border-b border-gray-800">11</td>
                <td class="px-2 py-1 border-b border-gray-800">5</td>
                <td class="px-2 py-1 border-b border-gray-800">6</td>
                <td class="px-2 py-1 border-b border-gray-800">5</td>
                <td class="px-2 py-1 border-b border-gray-800">6</td>
                <td class="px-2 py-1 border-b border-gray-800">~17%</td>
              </tr>
              <tr>
                <td class="px-2 py-1">12</td>
                <td class="px-2 py-1">5</td>
                <td class="px-2 py-1">7</td>
                <td class="px-2 py-1">5</td>
                <td class="px-2 py-1">7</td>
                <td class="px-2 py-1">~14%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <p><span class="text-indigo-400">หมายเหตุ:</span> ถ้าเป้าหมายมี 7 ห้อง แต่วิญญาณสามารถคัดออกได้ 5 ห้อง แต่จะคัดออกได้เพียงแค่ 2 ห้องเท่านั้นเพื่อไม่ให้โกงจนเกินไป</p>
    </div>
  </article>
</section>
<section class="space-y-6 mt-6">
  <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-shield-alt mr-2"></i> ระบบไอเทมวิญญาณผู้พิทักษ์
  </h2>
  <!-- Overview -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-info-circle mr-2"></i> ภาพรวมระบบวิญญาณผู้พิทักษ์
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">วิญญาณผู้พิทักษ์คืออะไร?</span> วิญญาณผู้พิทักษ์คือไอเทมประเภท 'วิญญาณ' ที่ผู้เล่นสามารถอัญเชิญเพื่อรับโบนัส stats และใช้งานเวทย์มนตร์พิเศษ โดยวิญญาณจะช่วยดูดซับความเสียหายจากการลอบสังหาร ก่อนที่ความเสียหายจะกระทบ HP จริง</p>
      <span>เมื่อมีผู้เล่นใช้งานไอเทมไล่ผี วิญญาณที่ถูกเรียกมาเฝ้าจะหายไปทันที</span>
    </div>
  </article>
  <!-- Usage -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-magic mr-2"></i> การใช้งานเวทย์มนตร์วิญญาณผู้พิทักษ์
    </h3>
  </article>
  <!-- Effect on Assassination -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-user-secret mr-2"></i> ผลกระทบต่อการลอบสังหาร
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">เกิดอะไรขึ้นเมื่อถูกโจมตี?</span> หากมีวิญญาณผู้พิทักษ์ใช้งานอยู่ ระบบจะใช้วิญญาณผู้พิทักษ์ เพื่อดูดซับ damage ก่อน หาก damage เกิน shield จะเหลือ damage เข้า HP จริง และ shield จะลดลงตามนั้น</p>
      <div>
        <span class="text-indigo-400">รายละเอียดการป้องกัน:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ลดลงตาม damage ที่ได้รับ</li>
          <li>หาก shield เหลือ 0 วิญญาณจะถูกทำลาย และ event วิญญาณผู้พิทักษ์ จะสิ้นสุด</li>
          <li>damage ที่เหลือหลังดูดซับจะเข้า HP ของผู้เล่น</li>
        </ul>
      </div>
      <p><span class="text-indigo-400">เคล็ดลับ:</span> วิญญาณผู้พิทักษ์ช่วยลดความเสี่ยงจากการถูกฆ่าตายในการลอบสังหาร โดยดูดซับ damage ก่อน!</p>
    </div>
  </article>
</section>
    <!-- ระบบชาวบ้าน -->
<section class="space-y-6 mt-6">
  <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-users mr-2"></i> ระบบชาวบ้าน
  </h2>
  <!-- Overview -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-info-circle mr-2"></i> ภาพรวมระบบชาวบ้าน
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ชาวบ้านคืออะไร?</span> ชาวบ้านคือ NPC ผู้รอดชีวิตที่พบได้ในอีเวนต์สำรวจแผนที่ สามารถจ้างกลับค่ายเพื่อใช้ในการยึดและป้องกัน outpost เท่านั้น </p>
      <div>
        <span class="text-indigo-400">ประโยชน์หลัก:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>- สามารถส่งไปบุกยึด Outpost</li>
          <li>- สามารถส่งไปป้องกัน Outpost</li>
        </ul>
      </div>
      <p><span class="text-indigo-400">ค่าย:</span> เก็บชาวบ้านได้สูงสุด 20 คน</p>
    </div>
  </article>
  <!-- Finding and Hiring -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-search mr-2"></i> การพบและจ้างชาวบ้าน
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">พบชาวบ้านได้ยังไง?</span> ในการสำรวจแผนที่สถานที่ต่างๆ</p>
      <div>
        <span class="text-indigo-400">การจ้าง:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>เลือก "จ้างชาวบ้าน" ในอีเวนต์</li>
          <li>หักค่าใช้จ่าย: Silver/Gold/Diamond ตาม ค่าจ้าง ของชาวบ้าน</li>
        </ul>
      </div>
    </div>
  </article>
  <!-- Camp System -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-home mr-2"></i> ระบบค่าย
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ค่ายทำงานยังไง?</span> ค่ายเก็บชาวบ้านสูงสุด 20 คน</p>
      <div>
        <span class="text-indigo-400">อัปเกรด capacity:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>1-15: Diamond 300 / Cash 100</li>
          <li>16-20: Diamond 600 / Cash 200</li>
          <li>+1 capacity ต่อครั้ง</li>
        </ul>
      </div>
    </div>
  </article>
  <!-- Level Upgrade -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-level-up-alt mr-2"></i> การอัปเกรดเลเวลชาวบ้าน
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">อัปเกรดเลเวลยังไง?</span> เลเวล 1-11 ได้ statPoints +20 ต่อเลเวล (รวม 200 แต้ม)</p>
      <div class="overflow-x-auto">
        <table class="min-w-full text-left text-xs border border-gray-700">
          <thead class="bg-gray-800">
            <tr>
              <th class="px-3 py-2 border-b border-gray-700">เลเวลปัจจุบัน</th>
              <th class="px-3 py-2 border-b border-gray-700">Diamond</th>
              <th class="px-3 py-2 border-b border-gray-700">Cash (เลเวล 10+ เท่านั้น)</th>
            </tr>
          </thead>
          <tbody class="text-indigo-200">
            <tr><td class="px-3 py-2 border-b border-gray-700">1-9</td><td class="px-3 py-2 border-b border-gray-700">เลเวล x 10</td><td class="px-3 py-2 border-b border-gray-700">-</td></tr>
            <tr><td class="px-3 py-2 border-b border-gray-700">10</td><td class="px-3 py-2 border-b border-gray-700">-</td><td class="px-3 py-2 border-b border-gray-700">30</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </article>
  <!-- Combat System -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-fist-raised mr-2"></i> ระบบการต่อสู้ชาวบ้าน (outpost)
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">การใช้งาน:</span> ส่งชาวบ้านไปยึด/ป้องกัน outpost</p>
      <div>
        <span class="text-indigo-400">Weapon & ทาง:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>Weapon: ดาบใหญ่, ดาบคู่, มีดสั้น, ธนู, เลื่อยไฟฟ้า, หอก, ถุงมือ, ค้อน, ขวาน, กระบอง</li>
          <li>strongAgainst/weakAgainst: กำหนด matchup (ชนะ = +1 dice 20%, แพ้ = -1 dice 20%)</li>
        </ul>
      </div>
      <p><span class="text-indigo-400">Dice:</span> attackDice (ฝ่ายบุก), defenseDice (ฝ่ายรับ) + perks</p>
    </div>
  </article>
  <!-- Perks -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-star mr-2"></i> Perks ของชาวบ้าน
    </h3>
    <div class="text-indigo-200 text-sm">
      <div class="overflow-x-auto">
        <table class="min-w-full text-left text-xs border border-gray-700">
          <thead class="bg-gray-800">
            <tr>
              <th class="px-3 py-2 border-b border-gray-700">ชื่อ Perk</th>
              <th class="px-3 py-2 border-b border-gray-700">คำอธิบาย</th>
              <th class="px-3 py-2 border-b border-gray-700">โอกาส</th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-b border-gray-800"><td class="px-3 py-2">ผู้รอดชีวิต</td><td class="px-3 py-2">ถ้าถูกฆ่าตายมีโอกาสกลับไปที่แคมป์</td><td class="px-3 py-2">50%</td></tr>
            <tr class="border-b border-gray-800"><td class="px-3 py-2">บ้าคลั่ง</td><td class="px-3 py-2">เลือด &lt;50% +1 dice</td><td class="px-3 py-2">20%</td></tr>
            <tr class="border-b border-gray-800"><td class="px-3 py-2">นักวางกับดัก</td><td class="px-3 py-2">ฝ่ายป้องกัน +1 dice</td><td class="px-3 py-2">20%</td></tr>
            <tr class="border-b border-gray-800"><td class="px-3 py-2">นักบุกโจมตี</td><td class="px-3 py-2">ฝ่ายโจมตี +1 dice</td><td class="px-3 py-2">20%</td></tr>
            <tr class="border-b border-gray-800"><td class="px-3 py-2">อึดถึกทน</td><td class="px-3 py-2">ลดดาเมจ 20%</td><td class="px-3 py-2">30%</td></tr>
            <tr class="border-b border-gray-800"><td class="px-3 py-2">ฟื้นตัวไว</td><td class="px-3 py-2">Heal 10% maxHP หลังรอบ</td><td class="px-3 py-2">10%</td></tr>
            <tr class="border-b border-gray-800"><td class="px-3 py-2">หลอกล่อ</td><td class="px-3 py-2">ลด dice ฝ่ายตรงข้าม 1</td><td class="px-3 py-2">20%</td></tr>
            <tr class="border-b border-gray-800"><td class="px-3 py-2">ผู้มีบุญ</td><td class="px-3 py-2">HP=0 ไม่ตาย และ heal ตัวเอง 20%</td><td class="px-3 py-2">10%</td></tr>
            <tr class="border-b border-gray-800"><td class="px-3 py-2">หัวหน้าทีม</td><td class="px-3 py-2">Heal ทีม 10% maxHP หลังรอบ</td><td class="px-3 py-2">10%</td></tr>
            <tr class="border-b border-gray-800"><td class="px-3 py-2">ฆาตกรโรคจิต</td><td class="px-3 py-2">+2 dice ถ้าทีม &lt;2 คน</td><td class="px-3 py-2">20%</td></tr>
            <tr class="border-b border-gray-800"><td class="px-3 py-2">นักเจรจา</td><td class="px-3 py-2">ทอยแพ้ ไม่โดนโจมตี</td><td class="px-3 py-2">10%</td></tr>
            <tr><td class="px-3 py-2">ผู้ใช้อาวุธระยะไกล</td><td class="px-3 py-2">+10% crit ถ้าแต้มนำ &gt;3</td><td class="px-3 py-2">100%</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </article>
</section>

<!-- ระบบ outpost -->
<section class="space-y-6 mt-6">
  <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-fort-awesome mr-2"></i> ระบบ outpost
  </h2>
  <!-- Update Announcement -->
  <div class="bg-yellow-900 bg-opacity-80 rounded-md p-4 border border-yellow-600 animate-fade-in mb-4">
    <h3 class="text-base font-semibold text-yellow-200 mb-2">
      <i class="fas fa-bullhorn mr-2"></i> อัปเดต 11/11/2568 เวลา 18:00 น. !!
    </h3>
    <div class="text-yellow-200 text-sm space-y-1">
      <p><strong>ระบบชาวบ้าน:</strong> perk ผู้รอดชีวิตเมื่อตายมีโอกาสกลับเข้าแคมป์ 50% จากปกติ 30%</p>
      <p><strong>ระบบ Outpost:</strong> เพิ่มระบบชาวบ้าน NPC ที่เคยครอบครอง Outpost กลับมาบุกทวง Outpost คืนจากผู้เล่น</p>
      <p><strong>ระบบเคลียร์ผู้ยึดครอง:</strong> ผู้เล่นสามารถเคลียร์ผู้ยึดครอง outpost เพื่อรับรางวัลเพชรและคะแนนยึดครอง</p>
      <p><strong>รางวัลป้องกัน NPC:</strong> เมื่อป้องกัน outpost จาก NPC สำเร็จ จะได้รับเพชรตาม dailyDiamondReward ของ outpost</p>
      <p><strong>NPC อัพเกรดอัตโนมัติ:</strong> ชาวบ้าน NPC ที่บุกทวงคืนจะถูกอัพเกรดอัตโนมัติระหว่างเลเวล 6-10 พร้อมสเตตัสที่เน้น AGI สูงสุด</p>
    </div>
  </div>
  <!-- Overview -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-info-circle mr-2"></i> ภาพรวมระบบ outpost
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ระบบ outpost คืออะไร?</span> ระบบ outpost เป็นระบบยึดครองพื้นที่พิเศษที่ผู้เล่นสามารถยึดครองเพื่อรับรางวัล เช่น เพชรรายวัน, คะแนนยึดครอง (occupationScore), และไอเทมจากกล่องสมบัติ ผู้เล่นยึดได้สูงสุด 2 แห่ง ใช้ชาวบ้านในการป้องกันและบุกยึด/เคลียร์ </p>
      <div>
        <span class="text-indigo-400">ประโยชน์หลัก:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>เปิดกล่องสมบัติวันละครั้ง (หลังคูลดาวน์ 3 ชม. หลังยึด) ได้ เพชร, คะแนน, ไอเทมในสมบัติ</li>
          <li><strong>บุกยึด:</strong> เปลี่ยนเจ้าของเอาท์โพสต์</li>
          <li><strong>เคลียร์ผู้ยึดครอง:</strong> ช่วย NPC เอาท์โพสต์คืน + รับรางวัลเพชรและคะแนนยึดครอง</li>
          <li>เอาท์โพสต์เมื่อถูกโจมตีจะสามารถถูกโจมตีได้อีกครั้งเมื่อ 1 ชั่วโมงถัดไป</li>
          <li><strong>ป้องกัน NPC:</strong> เมื่อชนะการบุกทวงคืนจาก NPC จะได้รับเพชรตาม dailyDiamondReward ของ outpost</li>
        </ul>
      </div>
      <p><span class="text-indigo-400">หมายเหตุ:</span> ชาวบ้านที่ส่งไปป้องกันเอาท์โพสต์ต้องเป็นชาวบ้านที่ชื่อไม่ซ้ำกัน</p>
    </div>
  </article>
  <!-- Assign Villagers -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-user-plus mr-2"></i> การส่งชาวบ้านไปป้องกันเอาท์โพสต์
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ส่งชาวบ้านยังไง?</span> เลือกชาวบ้านจากค่ายพักพิง เพื่อส่งไปป้องกันเอาท์โพสต์ที่ยึดครอง</p>
      <div>
        <span class="text-indigo-400">ประโยชน์ของฝ่ายป้องกัน:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ถ้าส่งชาวบ้านไปป้องกันเอาท์โพส 7 คน และ อีกฝ่ายส่งมา 10 คน จะสุ่มชาวบ้านที่ NPC เข้ามาช่วยป้องกันอีก 3 คน</li>
          <li>เมื่อชนะการบุกทวงคืนจาก NPC จะได้รับรางวัลเพชรตาม dailyDiamondReward ของ outpost</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">เงื่อนไข:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ชาวบ้านจะสามารถไปป้องกันได้เฉพาะแผนที่ที่ตัวเองรู้จักเท่านั้น สามารถดูได้ที่ปุ่มข้อมูลของชาวบ้าน ในเมนูแคมป์</li>
          <li>ชาวบ้านที่ส่งไปป้องกันเอาท์โพสต์ต้องเป็นชาวบ้านที่ชื่อไม่ซ้ำกัน</li>
          <li>ตรวจสอบให้ดีว่าเอาท์โพสต์ไหนสามารถส่งชาวบ้านป้องกันได้สูงสุดกี่คน</li>
        </ul>
      </div>
      
    </div>
  </article>
  <!-- Attack Outpost -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-swords mr-2"></i> การโจมตี outpost
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">โจมตียังไง?</span> เลือกชาวบ้านบุกเอาท์โพส จากนั้นเลือกยึดหรือเคลียร์ผู้ยึดครอง(ให้ npc มายึดแทน)</p>
      <div>
        <span class="text-indigo-400">รางวัลเคลียร์ผู้ยึดครอง:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>เพชร: dailyDiamondReward ของ outpost</li>
          <li>คะแนนยึดครอง: occupationScore ของ outpost</li>
          <li>outpost จะกลับสู่การควบคุมของ NPC</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">เงื่อนไข:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ชาวบ้านจะสามารถไปป้องกันได้เฉพาะแผนที่ที่ตัวเองรู้จักเท่านั้น สามารถดูได้ที่ปุ่มข้อมูลของชาวบ้าน ในเมนูแคมป์</li>
          <li>ชาวบ้านที่ส่งบึกยึดเอาท์โพสต์ต้องเป็นชาวบ้านที่ชื่อไม่ซ้ำกัน</li>
          <li>เมื่อส่งไปบุกยึดหรือเคลียร์แล้วต้องรอ 24 ชั่วโมงถึงจะสามารถส่งไปใหม่ได้</li>
          <li>ยึดได้มากสุด 3 เอาท์โพสต์</li>
        </ul>
      </div>
      <div class="overflow-x-auto mt-2">
        <table class="min-w-full text-left text-xs border border-gray-700">
          <thead class="bg-gray-800">
            <tr>
              <th class="px-3 py-2 border-b border-gray-700">สถานการณ์</th>
              <th class="px-3 py-2 border-b border-gray-700">โบนัส</th>
              <th class="px-3 py-2 border-b border-gray-700">รอบ</th>
              <th class="px-3 py-2 border-b border-gray-700">โอกาส</th>
            </tr>
          </thead>
          <tbody>
            <tr><td class="px-3 py-2 border-b border-gray-800">กับดักฝ่ายตั้งรับทำงาน</td><td class="px-3 py-2 border-b border-gray-800">defender +1 dice</td><td class="px-3 py-2 border-b border-gray-800">6</td><td class="px-3 py-2 border-b border-gray-800">5%</td></tr>
            <tr><td class="px-3 py-2 border-b border-gray-800">ฝนตกหนักหนักกระทันหัน</td><td class="px-3 py-2 border-b border-gray-800">defender +1 dice</td><td class="px-3 py-2 border-b border-gray-800">4</td><td class="px-3 py-2 border-b border-gray-800">15%</td></tr>
            <tr><td class="px-3 py-2 border-b border-gray-800">หน่วยลาดตระเวนเจอผู้กำลังบุกเข้าโจมตีทำให้ตั้งหลักได้ก่อน</td><td class="px-3 py-2 border-b border-gray-800">defender +1 dice</td><td class="px-3 py-2 border-b border-gray-800">2</td><td class="px-3 py-2 border-b border-gray-800">30%</td></tr>
            <tr><td class="px-3 py-2 border-b border-gray-800">ยามเฝ้าป้อมหลับ!!!</td><td class="px-3 py-2 border-b border-gray-800">attacker +1 dice</td><td class="px-3 py-2 border-b border-gray-800">4</td><td class="px-3 py-2 border-b border-gray-800">15%</td></tr>
            <tr><td class="px-3 py-2 border-b border-gray-800">เจอทางเข้าปริศนา</td><td class="px-3 py-2 border-b border-gray-800">attacker +1 dice</td><td class="px-3 py-2 border-b border-gray-800">6</td><td class="px-3 py-2 border-b border-gray-800">5%</td></tr>
            <tr><td class="px-3 py-2">ไม่มีสถานการณ์ใดๆเกิดขึ้น</td><td class="px-3 py-2">-</td><td class="px-3 py-2">0</td><td class="px-3 py-2">30%</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </article>
  <!-- NPC Reclaim -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-skull-crossbones mr-2"></i> การบุกทวงคืน outpost โดย NPC
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ระบบบุกทวงคืนคืออะไร?</span> ชาวบ้าน NPC ที่เคยครอบครอง outpost เดิมจะกลับมาบุกทวงคืนจากผู้เล่นที่ยึดครอง ผู้เล่นต้องป้องกันและทำ event ให้สำเร็จภายใน 24 ชั่วโมง มิเช่นนั้นชาวบ้านที่ส่งไปป้องกันจะตายทั้งหมด</p>
      <div>
        <span class="text-indigo-400">การทำงานของระบบ:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ทุกวันจะมีการสุ่ม 2 รอบ (ไม่สามารถบอกเวลาได้ล่วงหน้า)</li>
          <li>แต่ละสถานที่ (location) จะมี 1 outpost ที่ถูก NPC บุกทวงคืน</li>
          <li>outpost เดียวกันสามารถถูกบุกซ้ำได้</li>
          <li>เมื่อเกิด event ผู้เล่นจะได้รับ notification และ log เพื่อแจ้งเตือน</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">ระบบอัพเกรดอัตโนมัติของ NPC:</span>
        <ul class="list-disc pl-5 mt-1">
          <li><strong>เลเวลสุ่ม:</strong> ชาวบ้าน NPC จะถูกสุ่มเลเวลระหว่าง 6-10 อัตโนมัติ</li>
          <li><strong>สเตตัสพ้อยท์:</strong> จำนวนแต้มตามเลเวล (Level 6 = 100 points, Level 7 = 120 points, ... Level 10 = 180 points)</li>
          <li><strong>การกระจายสเตตัส:</strong> 
            <ul class="list-disc pl-5 mt-1">
              <li>AGI จะถูกอัพให้สูงสุด 99 ก่อนเสมอ</li>
              <li>แต้มที่เหลือจะถูกสุ่มแบ่งให้ stat อื่นๆ (stamina, str, int, luck, defense) แบบไม่เท่ากัน</li>
              <li>ตัวอย่าง: Level 10 → AGI 99, เหลือ 81 points แบ่งให้ stat อื่นแบบสุ่ม</li>
            </ul>
          </li>
          <li><strong>ความแข็งแกร่ง:</strong> NPC ที่บุกทวงคืนจะมีพลังมากกว่า NPC ทั่วไป ผู้เล่นต้องเตรียมชาวบ้านที่แข็งแกร่งไว้ป้องกัน</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">รางวัลเมื่อป้องกันสำเร็จ:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>เพชร: เท่ากับ dailyDiamondReward ของ outpost นั้นๆ</li>
          <li>ยังคงครอง outpost ต่อไป</li>
        </ul>
      </div>
      <div>
        <span class="text-indigo-400">เงื่อนไขและผลลัพธ์:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>event จะสุ่มสถานการณ์พิเศษ (special event) คล้ายกับการโจมตีปกติ (ดูตารางด้านบน)</li>
          <li><strong>หากชนะ:</strong> ผู้เล่นยังคงครอง outpost ต่อไป ชาวบ้าน NPC จะถูกกำจัด + รับรางวัลเพชร</li>
          <li><strong>หากแพ้หรือหมดเวลา:</strong> outpost จะถูกเคลียร์ให้ NPC ครองคืน ชาวบ้านผู้เล่นตายทั้งหมด</li>
          <li>ต้องใช้ชาวบ้านที่ส่งไปป้องกันเดิมในการต่อสู้ (ไม่สามารถเปลี่ยนได้ระหว่าง event)</li>
          <li><strong>คำแนะนำ:</strong> ควรส่งชาวบ้านที่มี AGI และ stats สูงไปป้องกันเพื่อรับมือกับ NPC ที่ถูกอัพเกรด</li>
        </ul>
      </div>
    </div>
  </article>
  <!-- Fight Round -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-dice mr-2"></i> การต่อสู้รอบใน outpost
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ต่อสู้ยังไง?</span>สุ่มชาวบ้านบุก/ป้องกัน ต่อสู้ทีละรอบ</p>
      <div>
        <span class="text-indigo-400">จะชนะได้อย่างไร:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>เมื่อฝ่ายใดฝ่ายหนึ่งเหลือชาวบ้านเป็นคนสุดท้าย</li>
        </ul>
        <span class="text-indigo-400">หมดเวลาจะเกิดอะไรขึ้น:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>ชาวบ้านของฝ่ายโจมตีจะตายทั้งหมด</li>
        </ul>
      </div>
    </div>
  </article>

</section>
    <!-- ระบบสิ่งมีชีวิตกลายพันธุ์ -->
<section class="space-y-6 mt-6">
  <h2 class="text-lg font-semibold text-indigo-200 mb-4">
    <i class="fas fa-biohazard mr-2"></i> ระบบสิ่งมีชีวิตกลายพันธุ์
  </h2>
  <!-- Overview -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-info-circle mr-2"></i> ภาพรวมระบบสิ่งมีชีวิตกลายพันธุ์
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">สิ่งมีชีวิตกลายพันธุ์คืออะไร?</span> สิ่งมีชีวิตกลายพันธุ์คือศัตรูที่พบได้ในอีเวนต์สำรวจแผนที่ สามารถโจมตีเพื่อฆ่าและรับรางวัล หรือจับได้ในบางชนิดเพื่อรับไอเทมพิเศษ</p>
      <div>
        <span class="text-indigo-400">ประโยชน์หลัก:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>- โจมตีเพื่อฆ่าและรับไอเทม รางวัลเงิน (Silver/Gold/Diamond)</li>
          <li>- จับเพื่อรับไอเทมสิ่งมีชีวิตกลายพันธุ์ที่อยู่ในกรง</li>
        </ul>
      </div>
      <p><span class="text-indigo-400">ระดับความหายาก (Rarity):</span> 1-5 (ยิ่งสูงยิ่งยากและรางวัลดี)</p>
    </div>
  </article>
  <!-- Finding -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-search mr-2"></i> การพบสิ่งมีชีวิตกลายพันธุ์
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">พบสิ่งมีชีวิตกลายพันธุ์ได้ยังไง?</span> ในการสำรวจแผนที่สถานที่ต่างๆ</p>
    </div>
  </article>
  <!-- Capture System -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-hand-paper mr-2"></i> การจับสิ่งมีชีวิตกลายพันธุ์
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">จับได้ยังไง?</span>  สิ่งมีชีวิตกลายพันธุ์ต้องเลือดน้อยกว่า 50% ของ Max HP</p>
      <div>
        <span class="text-indigo-400">เงื่อนไข:</span>
        <ul class="list-disc pl-5 mt-1">
          <li>- สิ่งมีชีวิตกลายพันธุ์แต่ละตัวจะถูกจับได้จำนวนครั้งไม่เท่ากัน ถ้าจับไม่สำเร็จตามที่กำหนดสิ่งมีชีวิตกลายพันธุ์จะหนีได้</li>
          <li>- ถ้าจับไม่สำเร็จ: ถูกโจมตี 100% โดยไม่สนค่าหลบหลีก (evasion) ของผู้เล่น</li>
          <li>- ถ้าถูกฆ่าตายจากการจับสิ่งมีชีวิตกลายพันธุ์ ผู้เล่นจะได้รับบทลงโทษ</li>
        </ul>
      </div>
      <p><span class="text-indigo-400">ถ้าจับสำเร็จ:</span> จะได้รับไอเทมตามที่กำหนด</p>
    </div>
  </article>
  <!-- Catch Difficulty -->
  <article class="bg-gray-900 bg-opacity-80 rounded-md p-4 border border-gray-600 animate-fade-in">
    <h3 class="text-base font-semibold text-indigo-200 mb-2">
      <i class="fas fa-exclamation-triangle mr-2"></i> ความยากในการจับ
    </h3>
    <div class="text-indigo-200 text-sm space-y-2">
      <p><span class="text-indigo-400">ระดับความยาก:</span> 1-5 (ง่ายถึงยาก)</p>
      <div class="overflow-x-auto">
        <table class="min-w-full text-left text-xs border border-gray-700">
          <thead class="bg-gray-800">
            <tr>
              <th class="px-3 py-2 border-b border-gray-700">ระดับ</th>
              <th class="px-3 py-2 border-b border-gray-700">โอกาสจับสำเร็จ</th>
              <th class="px-3 py-2 border-b border-gray-700">คำอธิบาย</th>
            </tr>
          </thead>
          <tbody class="text-indigo-200">
            <tr><td class="px-3 py-2 border-b border-gray-700">1</td><td class="px-3 py-2 border-b border-gray-700">5%</td><td class="px-3 py-2 border-b border-gray-700">ง่ายที่สุด</td></tr>
            <tr><td class="px-3 py-2 border-b border-gray-700">2</td><td class="px-3 py-2 border-b border-gray-700">4%</td><td class="px-3 py-2 border-b border-gray-700">ง่ายปานกลาง</td></tr>
            <tr><td class="px-3 py-2 border-b border-gray-700">3</td><td class="px-3 py-2 border-b border-gray-700">3%</td><td class="px-3 py-2 border-b border-gray-700">ปานกลาง</td></tr>
            <tr><td class="px-3 py-2 border-b border-gray-700">4</td><td class="px-3 py-2 border-b border-gray-700">2%</td><td class="px-3 py-2 border-b border-gray-700">ยากปานกลาง</td></tr>
            <tr><td class="px-3 py-2 border-b border-gray-700">5</td><td class="px-3 py-2 border-b border-gray-700">1%</td><td class="px-3 py-2 border-b border-gray-700">ยากที่สุด</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </article>
</section>
  </div>
  
</template>

<script>
import { ref } from 'vue';

export default {
  setup() {
    const rarityRequirements = ref([
  { level: '1', chance: '80%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 1 }], protectCost: { diamond: 200, silver: 20000 } },
  { level: '2', chance: '75%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 1 }], protectCost: { diamond: 300, silver: 30000 } },
  { level: '3', chance: '70%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 1 }], protectCost: { diamond: 400, silver: 40000 } },
  { level: '4', chance: '65%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 2 }], protectCost: { diamond: 500, silver: 50000 } },
  { level: '5', chance: '60%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 2 }], protectCost: { diamond: 600, silver: 60000 } },
  { level: '6', chance: '55%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 2 }], protectCost: { diamond: 700, silver: 70000 } },
  { level: '7', chance: '50%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 3 }], protectCost: { diamond: 800, silver: 80000 } },
  { level: '8', chance: '45%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 3 }], protectCost: { diamond: 900, silver: 90000 } },
  { level: '9', chance: '40%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 3 }], protectCost: { diamond: 1000, silver: 100000 } },
  { level: '10', chance: '80%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 4 }, { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 1 }], protectCost: { diamond: 1100, silver: 110000 } },
  { level: '11', chance: '80%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 4 }, { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 1 }], protectCost: { diamond: 1200, silver: 120000 } },
  { level: '12', chance: '75%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 4 }, { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 1 }], protectCost: { diamond: 1300, silver: 130000 } },
  { level: '13', chance: '70%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 5 }, { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 2 }], protectCost: { diamond: 1400, silver: 140000 } },
  { level: '14', chance: '65%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 5 }, { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 2 }], protectCost: { diamond: 1500, silver: 150000 } },
  { level: '15', chance: '60%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 5 }, { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 2 }], protectCost: { diamond: 1600, silver: 160000 } },
  { level: '16', chance: '55%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 6 }, { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 3 }], protectCost: { diamond: 1700, silver: 170000 } },
  { level: '17', chance: '50%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 6 }, { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 3 }], protectCost: { diamond: 1800, silver: 180000 } },
  { level: '18', chance: '45%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 6 }, { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 3 }], protectCost: { diamond: 1900, silver: 190000 } },
  { level: '19', chance: '40%', materials: [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 7 }, { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 4 }], protectCost: { diamond: 2000, silver: 200000 } },
]);

    return {
      rarityRequirements
    };
  }
};
</script>


<style scoped>
table {
  border-collapse: collapse;
  width: 100%;
}

th, td {
  border: 1px solid #4b5563;
  padding: 8px;
  text-align: left;
}

th {
  background-color: #374151;
  font-weight: 600;
}

td {
  background-color: #1f2937;
}

.overflow-x-auto {
  overflow-x: auto;
}

.animate-fade-in {
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Background styling */
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed;
}

/* Custom scrollbar */
.scrollbar-custom {
  scrollbar-width: thin;
  scrollbar-color: #4b5563 #374151;
}

.scrollbar-custom::-webkit-scrollbar {
  width: 5px;
}

.scrollbar-custom::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}

.scrollbar-custom::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}

.scrollbar-custom::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Button active state */
button:not(:disabled):active {
  transform: scale(0.98);
}

/* Fade-in animation */
.animate-fade-in {
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>